import React, { useCallback, useEffect, useState, useRef, Fragment, memo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, Crown, Building2, Users, Star, Receipt, Info, FileText, HandCoins, Globe, GraduationCap, Landmark, Sparkles, Play, Pause, SkipForward, Languages, ShieldCheck, Tv, Wind, Bus, HeartHandshake, BookOpen, Palette, Link2 } from 'lucide-react';
import { SlideAtmosphere } from '../components/deck/SlideAtmosphere';
import { PunjabiPlacard } from '../components/deck/PunjabiPlacard';
import { DelayedVideo } from '../components/deck/DelayedVideo';
const FlippableCard = ({
  card,
  isActive,
  onClick
}: {
  card: any;
  isActive?: boolean;
  onClick?: () => void;
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  // Reset flip state when card becomes inactive
  useEffect(() => {
    if (isActive === false) {
      setIsFlipped(false);
    }
  }, [isActive]);
  return <div className="aspect-[3/4] relative cursor-pointer group" style={{
    perspective: '1000px'
  }} onClick={() => {
    if (onClick) onClick();
    if (isActive !== false) setIsFlipped(!isFlipped);
  }} data-id="element-7665" data-mp="19qxtpsgjizmf">
      <motion.div className="w-full h-full relative" animate={{
      rotateY: isFlipped ? 180 : 0
    }} transition={{
      duration: 0.8,
      type: 'spring',
      stiffness: 200,
      damping: 25
    }} style={{
      transformStyle: 'preserve-3d'
    }} data-id="element-7666" data-mp="1nriakj2walda">
        {/* Front */}
        <div className="absolute inset-0 flex flex-col items-center justify-center overflow-hidden shadow-[0_0_30px_rgba(212,175,55,0.15)] bg-black" style={{
        backfaceVisibility: 'hidden'
      }} data-id="element-7667" data-mp="zzl0oxj0a3et">
          <img src={card.src} alt={card.name} className="w-full h-full object-cover" data-id="element-7668" data-mp="cb1r8eo7zkl5" />
        </div>

        {/* Back */}
        <div className="absolute inset-0 bg-[#0a0604] border border-[#D4AF37]/40 flex flex-col items-center justify-center p-5 text-center shadow-[inset_0_0_40px_rgba(0,0,0,0.8)]" style={{
        transform: 'rotateY(180deg)',
        backfaceVisibility: 'hidden'
      }} data-id="element-7669" data-mp="1anntg5bkbfzq">
          <div className="absolute inset-0 opacity-10 bg-[url('https://cdn.magicpatterns.com/uploads/3hBK2oMvmeZZy5P54xmeKQ/Lahore_Court_Collection.png')] bg-cover bg-center mix-blend-overlay" data-id="element-7670" data-mp="wnd3r5lzo7g5" />
          <div className="relative z-10 w-full flex flex-col items-center" data-id="element-7671" data-mp="15p2su4seeuar">
            <Crown className="w-5 h-5 text-[#D4AF37] mx-auto mb-2 opacity-80 shrink-0" data-id="element-7672" data-mp="15m6xmo7xqjq0" />
            <h4 className="text-[#FDFBF7] font-serif text-base sm:text-lg mb-2 shrink-0" data-id="element-7673" data-mp="10rkqix8vzdbq">
              {card.name}
            </h4>
            <div className="w-12 h-px bg-[#D4AF37]/50 mx-auto mb-3 shrink-0" data-id="element-7674" data-mp="2c5526llwwb28" />
            <p className="text-white/75 text-[0.62rem] sm:text-[0.7rem] font-light leading-relaxed mb-3 text-left" data-id="element-7675" data-mp="2klnk8378jy4w">
              {card.desc}
            </p>
            {card.stats && <div className="grid grid-cols-2 gap-3 text-left bg-black/40 p-2.5 border border-[#D4AF37]/20 backdrop-blur-sm w-full shrink-0" data-id="element-7676" data-mp="2okweraa3o01i">
                {card.stats.map((stat: any, idx: number) => <div key={idx} data-id="element-7677" data-mp="33c5be3cf6lo5" data-mpi={idx}>
                    <p className="text-[#D4AF37] text-[9px] uppercase tracking-widest mb-0.5" data-id="element-7678" data-mp="33bvjubbq86gc">
                      {stat.label}
                    </p>
                    <p className="text-white/90 text-xs font-light" data-id="element-7679" data-mp="38d1eieoy63si">
                      {stat.value}
                    </p>
                  </div>)}
              </div>}
          </div>
        </div>
      </motion.div>
    </div>;
};
const TreasuryFan = ({
  cards
}: {
  cards: any[];
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  return <div className="relative w-full max-w-4xl mx-auto h-[320px] flex justify-center items-end" data-id="element-7680" data-mp="3r3j44aehgj9n">
      {/* Backdrop to dismiss active card */}
      <AnimatePresence data-id="element-7681" data-mp="1ye1wpzwbahfy">
        {activeIndex !== null && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm cursor-pointer" onClick={() => setActiveIndex(null)} data-id="element-7682" data-mp="1ygo2btzn6mvj" />}
      </AnimatePresence>

      {cards.map((card, i) => {
      const isActive = activeIndex === i;
      const offset = i - Math.floor(cards.length / 2);
      const rotation = isActive ? 0 : offset * 12;
      const yOffset = isActive ? -120 : Math.abs(offset) * 15;
      // Use window.innerWidth to determine if mobile for xOffset, or just use a smaller base offset
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
      const xOffset = isActive ? 0 : offset * (isMobile ? 40 : 80);
      const zIndex = isActive ? 50 : 10 + i;
      return <motion.div key={i} className="absolute bottom-0 w-32 sm:w-48 md:w-56" style={{
        zIndex
      }} animate={{
        rotate: rotation,
        y: yOffset,
        scale: isActive ? isMobile ? 1.2 : 1.4 : 1,
        x: xOffset
      }} transition={{
        type: 'spring',
        stiffness: 260,
        damping: 25
      }} data-id="element-7683" data-mp="3ceeqwfj9eleq" data-mpi={i}>
            <FlippableCard card={card} isActive={isActive} onClick={() => {
          if (!isActive) {
            setActiveIndex(i);
          }
        }} data-id="element-7684" data-mp="3zeawjxan37d" />
          </motion.div>;
    })}
    </div>;
};
const FounderCoinCarousel = ({
  coins
}: {
  coins: {
    src: string;
    label: string;
  }[];
}) => {
  const [index, setIndex] = useState(0);
  const go = (dir: number) => setIndex(prev => (prev + dir + coins.length) % coins.length);
  return <div className="w-full flex flex-col items-center" data-id="element-7685" data-mp="29xktt225ixn5">
      <div className="relative w-full max-w-4xl px-4 sm:px-0" data-id="element-7686" data-mp="2nrlsjvt3skni">
        {/* Large coin image */}
        <div className="relative aspect-square sm:aspect-[3/2] w-full overflow-hidden border border-[#D4AF37]/20 bg-black shadow-[0_0_60px_rgba(212,175,55,0.15)]" data-id="element-7687" data-mp="2izaiw18k9khr">
          <AnimatePresence mode="wait" data-id="element-7688" data-mp="2sqevwveoev9y">
            <motion.img key={index} src={coins[index].src} alt={coins[index].label} initial={{
            opacity: 0,
            scale: 1.02
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.98
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} className="absolute inset-0 w-full h-full object-contain p-4 sm:p-0" data-id="element-7689" data-mp="2spdjami0i0q4" />
          </AnimatePresence>
        </div>

        {/* Arrows */}
        <button type="button" onClick={() => go(-1)} aria-label="Previous coin" className="absolute left-6 sm:-left-6 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-12 sm:h-12 rounded-full border border-[#D4AF37]/40 bg-black/70 backdrop-blur-sm flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors" data-id="element-7690" data-mp="ofiosnitbext">
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-7691" data-mp="5ihi8y9m16rr" />
        </button>
        <button type="button" onClick={() => go(1)} aria-label="Next coin" className="absolute right-6 sm:-right-6 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-12 sm:h-12 rounded-full border border-[#D4AF37]/40 bg-black/70 backdrop-blur-sm flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors" data-id="element-7692" data-mp="ag97fkivees9">
          <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-7693" data-mp="38fwcodierlin" />
        </button>
      </div>

      {/* Caption + dots */}
      <p className="text-[#D4AF37] text-[10px] sm:text-sm tracking-[0.2em] uppercase mt-5 text-center px-4" data-id="element-7694" data-mp="33oxs31qe019l">
        {coins[index].label}
      </p>
      <div className="flex items-center gap-2 mt-3" data-id="element-7695" data-mp="2zn2ry0k6ztxe">
        {coins.map((_, i) => <button key={i} type="button" onClick={() => setIndex(i)} aria-label={`View coin ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === index ? 'w-4 sm:w-6 bg-[#D4AF37]' : 'w-1.5 bg-white/30 hover:bg-white/50'}`} data-id="element-7696" data-mp="2upar2h1kkxh2" data-mpi={i} />)}
      </div>
    </div>;
};
const DailyPacketAnimation = () => {
  const [packetState, setPacketState] = useState<'unopened' | 'opening' | 'revealed'>('unopened');
  return <div className="flex flex-col items-center justify-center w-full h-[560px] relative" data-id="element-7697" data-mp="eyolk0nvq425">
      <AnimatePresence mode="wait" data-id="element-7698" data-mp="1wtxdbet4qjl0">
        {packetState === 'unopened' && <motion.div key="unopened" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 1.1
      }} className="flex flex-col items-center text-center max-w-md mx-auto relative" data-id="element-7699" data-mp="21tjn1e9r1hp2">
            <motion.div className="relative w-64 sm:w-72 mb-8" data-id="element-7700" data-mp="1iw8ra3nbt6ed">
              <motion.div animate={{
            scale: [1, 1.05, 1],
            opacity: [0.5, 0.8, 0.5]
          }} transition={{
            duration: 3,
            repeat: Infinity
          }} className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.35),_transparent_65%)] blur-2xl pointer-events-none" data-id="element-7701" data-mp="1mwspzbnjsi2m" />
              <motion.button type="button" onClick={() => {
            setPacketState('opening');
            setTimeout(() => setPacketState('revealed'), 1900);
          }} whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.96
          }} className="relative block w-full cursor-pointer" data-id="element-7702" data-mp="i99i31rd6jr7">
                <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Sealed envelope" className="w-full drop-shadow-[0_25px_50px_rgba(0,0,0,0.7)]" data-id="element-7703" data-mp="4a9ulxfl8awm" />
                <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase whitespace-nowrap" data-id="element-7704" data-mp="dcj6aqmleabl">
                  Click to open
                </span>
              </motion.button>
            </motion.div>
          </motion.div>}

        {packetState === 'opening' && <motion.div key="opening" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative flex items-center justify-center w-full h-full" data-id="element-7705" data-mp="356ssiyfm35ze">
            <motion.div initial={{
          scale: 0.4,
          opacity: 0
        }} animate={{
          scale: 4,
          opacity: [0, 0.9, 0]
        }} transition={{
          duration: 1.8,
          ease: 'easeOut'
        }} className="absolute w-96 h-96 rounded-full bg-[radial-gradient(circle,_rgba(212,175,55,0.6),_transparent_60%)] blur-3xl" data-id="element-7706" data-mp="2qi68wkiybs2d" />
            <motion.div initial={{
          scale: 1,
          opacity: 1
        }} animate={{
          scale: [1, 1.1, 0.6],
          opacity: [1, 1, 0],
          rotateZ: [0, -3, 4, -2, 0]
        }} transition={{
          duration: 1.8,
          times: [0, 0.5, 1],
          ease: 'easeInOut'
        }} className="relative w-64 sm:w-72 z-10" data-id="element-7707" data-mp="aq8r1fxr2ikq">
              <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Opening envelope" className="w-full drop-shadow-[0_0_80px_rgba(212,175,55,0.7)]" data-id="element-7708" data-mp="1diupoqiq3elq" />
            </motion.div>
            {Array.from({
          length: 20
        }).map((_, i) => {
          const angle = i / 20 * Math.PI * 2;
          const distance = 280;
          return <motion.div key={i} initial={{
            x: 0,
            y: 0,
            opacity: 0,
            scale: 0
          }} animate={{
            x: Math.cos(angle) * distance,
            y: Math.sin(angle) * distance,
            opacity: [0, 1, 0],
            scale: [0, 1.2, 0]
          }} transition={{
            duration: 1.6,
            delay: 0.3 + i % 4 * 0.05,
            ease: 'easeOut'
          }} className="absolute w-2 h-2 bg-[#D4AF37] rounded-full shadow-[0_0_14px_rgba(212,175,55,1)]" data-id="element-7709" data-mp="3ods65xi0s9il" data-mpi={i} />;
        })}
          </motion.div>}

        {packetState === 'revealed' && <motion.div key="revealed" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="flex flex-col items-center justify-center" data-id="element-7710" data-mp="3711yoo2cg9k2">
            <div className="flex justify-center mb-8" data-id="element-7711" data-mp="2s1hca64km7w6">
              <motion.div initial={{
            y: 40,
            opacity: 0,
            rotate: 0
          }} animate={{
            y: 0,
            opacity: 1,
            rotate: 0
          }} transition={{
            type: 'spring',
            delay: 0.1
          }} className="w-48 aspect-[3/4]" data-id="element-7712" data-mp="2wyzp47lpl79g">
                <img src="https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png" className="w-full h-full object-cover shadow-2xl" alt="Card" data-id="element-7713" data-mp="3fnwa1kv7unf0" />
              </motion.div>
            </div>
            <motion.button initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1
        }} onClick={() => setPacketState('unopened')} className="px-6 py-2 border border-[#D4AF37]/30 text-[#D4AF37] text-xs tracking-[0.2em] uppercase hover:bg-[#D4AF37]/10 transition-colors" data-id="element-7714" data-mp="1qc44xykd56i3">
              Reset Animation
            </motion.button>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export const SponsorDeckPage = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [archiveIndex, setArchiveIndex] = useState(0);
  const [drinkIndex, setDrinkIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [slide7AudioPlaying, setSlide7AudioPlaying] = useState(false);
  const slide7AudioRef = useRef<HTMLAudioElement>(null);
  const slide7VideoRef = useRef<HTMLVideoElement>(null);
  const slide7TimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [slide7bAudioPlaying, setSlide7bAudioPlaying] = useState(false);
  const slide7bAudioRef = useRef<HTMLAudioElement>(null);
  const slide7bVideoRef = useRef<HTMLVideoElement>(null);
  const slide7bTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSlide7Play = () => {
    if (slide7AudioPlaying) {
      // Pause
      if (slide7AudioRef.current) slide7AudioRef.current.pause();
      if (slide7VideoRef.current) slide7VideoRef.current.pause();
      if (slide7TimeoutRef.current) clearTimeout(slide7TimeoutRef.current);
      setSlide7AudioPlaying(false);
    } else {
      // Play
      if (isPlaying) {
        if (audioRef.current) audioRef.current.pause();
        setIsPlaying(false);
      }
      if (slide7AudioRef.current) {
        slide7AudioRef.current.play();
        setSlide7AudioPlaying(true);
        if (slide7VideoRef.current) {
          if (slide7VideoRef.current.currentTime === 0) {
            slide7TimeoutRef.current = setTimeout(() => {
              if (slide7VideoRef.current) {
                slide7VideoRef.current.playbackRate = 0.5;
                slide7VideoRef.current.play();
              }
            }, 10000);
          } else {
            slide7VideoRef.current.playbackRate = 0.5;
            slide7VideoRef.current.play();
          }
        }
      }
    }
  };
  const handleSlide7bPlay = () => {
    if (slide7bAudioPlaying) {
      // Pause
      if (slide7bAudioRef.current) slide7bAudioRef.current.pause();
      if (slide7bVideoRef.current) slide7bVideoRef.current.pause();
      if (slide7bTimeoutRef.current) clearTimeout(slide7bTimeoutRef.current);
      setSlide7bAudioPlaying(false);
    } else {
      // Play
      if (isPlaying) {
        if (audioRef.current) audioRef.current.pause();
        setIsPlaying(false);
      }
      if (slide7bAudioRef.current) {
        slide7bAudioRef.current.play();
        setSlide7bAudioPlaying(true);
        if (slide7bVideoRef.current) {
          if (slide7bVideoRef.current.currentTime === 0) {
            slide7bTimeoutRef.current = setTimeout(() => {
              if (slide7bVideoRef.current) {
                slide7bVideoRef.current.playbackRate = 0.5;
                slide7bVideoRef.current.play();
              }
            }, 10000);
          } else {
            slide7bVideoRef.current.playbackRate = 0.5;
            slide7bVideoRef.current.play();
          }
        }
      }
    }
  };
  useEffect(() => {
    const video = slide7VideoRef.current;
    if (!video) return;
    let reverseIntervalId: ReturnType<typeof setInterval>;
    let reverseTimeoutId: ReturnType<typeof setTimeout>;
    const reversePlay = () => {
      if (!video) return;
      if (video.currentTime <= 0.1) {
        // Reached the beginning, stop reversing and play forward again
        clearInterval(reverseIntervalId);
        video.playbackRate = 0.5;
        video.play();
        return;
      }
      // Only step back if the video isn't currently busy seeking
      // This prevents Chrome from stalling when stepping backwards
      if (!video.seeking) {
        // Step backwards by 0.05s every 100ms (0.5x speed)
        video.currentTime = Math.max(0, video.currentTime - 0.05);
      }
    };
    const handleEnded = () => {
      video.pause(); // Ensure forward playback is stopped
      // Wait 5 seconds before reversing
      reverseTimeoutId = setTimeout(() => {
        reverseIntervalId = setInterval(reversePlay, 100);
      }, 5000);
    };
    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('ended', handleEnded);
      if (reverseIntervalId) clearInterval(reverseIntervalId);
      if (reverseTimeoutId) clearTimeout(reverseTimeoutId);
    };
  }, []);
  useEffect(() => {
    const video = slide7bVideoRef.current;
    if (!video) return;
    let reverseIntervalId: ReturnType<typeof setInterval>;
    let reverseTimeoutId: ReturnType<typeof setTimeout>;
    const reversePlay = () => {
      if (!video) return;
      if (video.currentTime <= 0.1) {
        // Reached the beginning, stop reversing and play forward again
        clearInterval(reverseIntervalId);
        video.playbackRate = 0.5;
        video.play();
        return;
      }
      // Only step back if the video isn't currently busy seeking
      // This prevents Chrome from stalling when stepping backwards
      if (!video.seeking) {
        // Step backwards by 0.05s every 100ms (0.5x speed)
        video.currentTime = Math.max(0, video.currentTime - 0.05);
      }
    };
    const handleEnded = () => {
      video.pause(); // Ensure forward playback is stopped
      // Wait 5 seconds before reversing
      reverseTimeoutId = setTimeout(() => {
        reverseIntervalId = setInterval(reversePlay, 100);
      }, 5000);
    };
    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('ended', handleEnded);
      if (reverseIntervalId) clearInterval(reverseIntervalId);
      if (reverseTimeoutId) clearTimeout(reverseTimeoutId);
    };
  }, []);
  const tracks = ['https://cdn.magicpatterns.com/uploads/i6gGeaSuUDdxcvUXNfouoe/Dusk_at_the_Walled_Gate.mp3', 'https://cdn.magicpatterns.com/uploads/xqFvTV8Sq5Sxo7k3BZ1pu2/2._Maharaja_Ranjit_Singh_Invite_to_Enter_Quest__Lion_of_the_Five_Rivers.mp3'];
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  const nextTrack = () => {
    setCurrentTrackIndex(prev => (prev + 1) % tracks.length);
    setIsPlaying(true);
  };
  useEffect(() => {
    if (audioRef.current && isPlaying) {
      audioRef.current.play().catch(() => setIsPlaying(false));
    }
  }, [currentTrackIndex]);
  const totalSlides = 35;
  const nextSlide = useCallback(() => {
    setActiveSlide(prev => Math.min(prev + 1, totalSlides - 1));
  }, []);
  const prevSlide = useCallback(() => {
    setActiveSlide(prev => Math.max(prev - 1, 0));
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') nextSlide();else if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  useEffect(() => {
    const archiveInterval = setInterval(() => {
      setArchiveIndex(prev => (prev + 1) % 4);
    }, 5000);
    const drinkInterval = setInterval(() => {
      setDrinkIndex(prev => (prev + 1) % 5);
    }, 3000);
    return () => {
      clearInterval(archiveInterval);
      clearInterval(drinkInterval);
    };
  }, []);
  return <div className="bg-black w-screen h-screen overflow-hidden text-white relative font-sans" data-id="element-7715" data-mp="2gcqbbnm0t3kx">
      {/* Fixed Chrome */}
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-[#D4AF37] origin-left z-50" initial={{
      scaleX: 0
    }} animate={{
      scaleX: activeSlide / (totalSlides - 1)
    }} transition={{
      duration: 0.5,
      ease: 'easeInOut'
    }} data-id="element-7716" data-mp="2bdwx1sywt8jp" />

      <Link to="/" className="fixed top-8 left-8 z-40 flex items-center gap-3 text-white/50 hover:text-[#D4AF37] transition-colors text-xs tracking-widest uppercase font-light" data-id="element-7717" data-mp="3kdg4l1eg6zqr">
        <ArrowLeft className="w-4 h-4" data-id="element-7718" data-mp="gx0qraraobah" />
        Home
      </Link>

      <div className="fixed top-4 sm:top-8 right-4 sm:right-8 z-40 flex flex-col items-end gap-2 sm:gap-3" data-id="element-7719" data-mp="kvqcbqmhkz57">
        <div className="text-[#D4AF37]/60 text-[10px] sm:text-xs font-light tracking-[0.3em]" data-id="element-7720" data-mp="74l80b86bioj">
          {String(activeSlide + 1).padStart(2, '0')} / {totalSlides}
        </div>
        <div className="flex items-center gap-2" data-id="element-7721" data-mp="zshbpzw5uaut">
          <button onClick={togglePlay} className="w-6 h-6 sm:w-8 sm:h-8 rounded-full border border-[#D4AF37]/20 bg-black/40 backdrop-blur-md flex items-center justify-center text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 transition-all duration-300" data-id="element-7722" data-mp="14k8uho92rcae">
            {isPlaying ? <Pause className="w-2 h-2 sm:w-3 sm:h-3" data-id="element-7723" data-mp="332caj1teluyu" /> : <Play className="w-2 h-2 sm:w-3 sm:h-3 ml-0.5" data-id="element-7724" data-mp="2y4130xqkzwjs" />}
          </button>
          <button onClick={nextTrack} className="w-6 h-6 sm:w-8 sm:h-8 rounded-full border border-[#D4AF37]/20 bg-black/40 backdrop-blur-md flex items-center justify-center text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 transition-all duration-300" data-id="element-7725" data-mp="3uuaa545ueajo">
            <SkipForward className="w-2 h-2 sm:w-3 sm:h-3" data-id="element-7726" data-mp="3h2wrrtjo7iq8" />
          </button>
        </div>
      </div>

      <audio ref={audioRef} src={tracks[currentTrackIndex]} loop className="hidden" data-id="element-7727" data-mp="icwqcpoox4i6" />

      <div className="fixed bottom-4 sm:bottom-8 right-4 sm:right-8 z-40 hidden sm:flex items-center gap-4" data-id="element-7728" data-mp="dlwpj9cga2ae">
        <button onClick={prevSlide} disabled={activeSlide === 0} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-[#D4AF37]/20 bg-black/40 backdrop-blur-md flex items-center justify-center text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 disabled:opacity-20 transition-all duration-300" data-id="element-7729" data-mp="kycrzlg8w14u">
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-7730" data-mp="13w6ynstwi0k2" />
        </button>
        <button onClick={nextSlide} disabled={activeSlide === totalSlides - 1} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-[#D4AF37]/20 bg-black/40 backdrop-blur-md flex items-center justify-center text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 disabled:opacity-20 transition-all duration-300" data-id="element-7731" data-mp="13vx8o58uvted">
          <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-7732" data-mp="1mnpyxrwjj0ie" />
        </button>
      </div>

      {/* Slides Container */}
      <motion.div className="flex h-full w-full" drag="x" dragConstraints={{
      left: 0,
      right: 0
    }} dragElastic={0.2} onDragEnd={(e, {
      offset,
      velocity
    }) => {
      const swipeThreshold = 50;
      if (offset.x < -swipeThreshold || velocity.x < -500) nextSlide();else if (offset.x > swipeThreshold || velocity.x > 500) prevSlide();
    }} animate={{
      x: `-${activeSlide * 100}vw`
    }} transition={{
      type: 'spring',
      stiffness: 150,
      damping: 25,
      mass: 0.8
    }} data-id="element-7733" data-mp="1rmazjsfpfm7d">
        {/* 1. Cover */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7734" data-mp="k8pf4ckjwv7s">
          <SlideAtmosphere imageUrl="https://cdn.magicpatterns.com/uploads/7ngvtUG3Hhfrt7dJK3EcMQ/Gemini_Generated_Image_b3829nb3829nb382.png" opacity={0.5} overlayGradient="gold" animate="ken-burns-loop" withParticles data-id="element-7735" data-mp="fa5uqgllvrhj" />
          <PunjabiPlacard word="ਸ਼ੁਰੂਆਤ" translation="Beginning" data-id="element-7736" data-mp="wi5f2b74qohq" />

          <div className="relative z-10 text-center px-4 sm:px-6 max-w-4xl mx-auto" data-id="element-7737" data-mp="11hi7dbgrsnrp">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2,
            ease: 'easeOut'
          }} data-id="element-7738" data-mp="11jcjc0a2d5jn">
              <h1 className="font-serif text-5xl sm:text-7xl md:text-8xl text-[#FDFBF7] mb-4 sm:mb-6 leading-none drop-shadow-2xl" data-id="element-7739" data-mp="irabnefegzlj">
                Kingdoms of Punjab
              </h1>
              <p className="text-sm sm:text-xl md:text-2xl text-[#D4AF37] font-light tracking-[0.2em] uppercase mb-6 sm:mb-8" data-id="element-7740" data-mp="8sb0np75wrvd">
                Life in the Sikh Kingdoms
              </p>
              <div className="w-px h-12 sm:h-16 bg-gradient-to-b from-[#D4AF37] to-transparent mx-auto" data-id="element-7741" data-mp="236vkjznnsjdp" />
            </motion.div>
          </div>
        </section>

        {/* 2. Opening Truth */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7742" data-mp="lyfbnc76b8u1">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7743" data-mp="h770aama9de8" />
          <PunjabiPlacard word="ਵਿਰਾਸਤ" translation="Heritage" data-id="element-7744" data-mp="h5cg4yiu5pb7" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7745" data-mp="c82uzv2hbzt6">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2
          }} data-id="element-7746" data-mp="87ai8ruefcs5">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-tight mb-8" data-id="element-7747" data-mp="3pg79v8bwkp0o">
                We inherited a civilization.
                <br data-id="element-7748" data-mp="1skrloqp41phx" />
                <span className="text-[#D4AF37]" data-id="element-7749" data-mp="1no0x1ez8plkw">
                  Our children inherited fragments.
                </span>
              </h2>
              <p className="text-lg text-white/60 font-light max-w-2xl mx-auto" data-id="element-7750" data-mp="1w40a0gg0ieqy">
                When a people forget their history, they risk losing the courage
                and dignity it was meant to give them.
              </p>
            </motion.div>

            <motion.figure initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.5,
            delay: 0.3
          }} className="mt-16 pt-10 border-t border-[#D4AF37]/15" data-id="element-7751" data-mp="29vwwo4ywvwfy">
              <blockquote className="font-serif text-xl sm:text-2xl text-[#D4AF37] italic leading-relaxed" data-id="element-7752" data-mp="1m5aqkj8gwm8l">
                "A people without knowledge of their past history, origin and
                culture is like a tree without roots."
              </blockquote>
              <figcaption className="text-white/40 text-xs tracking-[0.3em] uppercase mt-6" data-id="element-7753" data-mp="32jsrt58u1gam">
                — Winston Churchill
              </figcaption>
            </motion.figure>
          </div>
        </section>

        {/* 3. ROM Legacy */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7754" data-mp="1byswha6azc58">
          <SlideAtmosphere imageUrl="https://cdn.magicpatterns.com/uploads/mdkRi9opWdPpM6cSCo7Zgm/image.png" opacity={0.2} overlayGradient="dark" data-id="element-7755" data-mp="134eirc1f89o8" />
          <PunjabiPlacard word="ਪ੍ਰਮਾਣ" translation="Proof" data-id="element-7756" data-mp="t6gwuv71aoys" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center gap-16" data-id="element-7757" data-mp="t7s08twkhupd">
            <motion.div className="flex-1" initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7758" data-mp="f8scrpksjlus">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-6" data-id="element-7759" data-mp="2on8hswypli1q">
                The Precedent
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-tight mb-6" data-id="element-7760" data-mp="2jpyi8ohlvq06">
                Our community has proven this story matters.
              </h2>
              <p className="text-lg text-white/60 font-light" data-id="element-7761" data-mp="2sp3grut63jcc">
                <span className="text-[#D4AF37]" data-id="element-7762" data-mp="2sniq4g9z4kjr">
                  Arts of the Sikh Kingdoms
                </span>{' '}
                remains the second most successful exhibition in Canadian
                history at the Royal Ontario Museum.
              </p>
            </motion.div>
            <motion.div className="flex-1 w-full max-w-sm" initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7763" data-mp="3gctojr3uq2uq">
              <div className="aspect-[3/4] p-3 bg-[#110c08] border border-[#D4AF37]/20 shadow-[0_20px_50px_rgba(0,0,0,0.5)]" data-id="element-7764" data-mp="3h5d0wsr1k5ic">
                <img src="https://cdn.magicpatterns.com/uploads/mdkRi9opWdPpM6cSCo7Zgm/image.png" alt="ROM Exhibition" className="w-full h-full object-cover transition-all duration-700 hover:scale-105" data-id="element-7765" data-mp="3m58tzwy7acj4" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* 4. Epiphany & Trailer */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex flex-col items-center justify-center" data-id="element-7766" data-mp="8utcm8veyqw8">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7767" data-mp="imfw0shwnwa2" />
          <PunjabiPlacard word="ਜਾਗ੍ਰਿਤੀ" translation="Awakening" data-id="element-7768" data-mp="ilwfng7wlfy5" />

          <div className="relative z-10 max-w-6xl mx-auto w-full px-6 text-center" data-id="element-7769" data-mp="nmbr228mkddh">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} data-id="element-7770" data-mp="rmm5826kma3f">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-12" data-id="element-7771" data-mp="233yj795w5mky">
                What if Sikh history could feel alive?
              </h2>
              <div className="w-full max-w-4xl mx-auto aspect-video bg-black border border-[#D4AF37]/20 shadow-[0_0_50px_rgba(212,175,55,0.1)] relative p-1" data-id="element-7772" data-mp="1y7hw8iafhe9g">
                <iframe src="https://www.youtube.com/embed/2yMWvJinVgk" title="Trailer" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen" allowFullScreen className="w-full h-full" data-id="element-7773" data-mp="2qv3ynn7x6cuq" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* 5. Vision */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7774" data-mp="2uax4gs985ur9">
          <SlideAtmosphere imageUrl="https://uploadthingy.s3.us-west-1.amazonaws.com/rru8mxg1f864Swgj1TtDBp/Gemini_Generated_Image_xf598lxf598lxf59.png" opacity={0.15} overlayGradient="gold" data-id="element-7775" data-mp="2yd0hvmfgf9f6" />
          <PunjabiPlacard word="ਮੰਜ਼ਿਲ" translation="Destination" data-id="element-7776" data-mp="2pccavboha9bs" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7777" data-mp="3d151csmbzoz0">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7778" data-mp="3i086se683x7i">
              <p className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-8" data-id="element-7779" data-mp="3vzzg41lsopf6">
                The Goal
              </p>
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] leading-tight mb-8" data-id="element-7780" data-mp="4n66jub3i9n3">
                The most successful immersive experience in{' '}
                <span className="text-white/35 line-through decoration-[#D4AF37] decoration-2" data-id="element-7781" data-mp="1kdfiifea4z5m">
                  Canadian
                </span>{' '}
                <span className="text-[#D4AF37]" data-id="element-7782" data-mp="1y3iumdjg3chc">North American</span> history.
              </h2>
              <div className="w-px h-12 bg-gradient-to-b from-[#D4AF37]/50 to-transparent mx-auto" data-id="element-7783" data-mp="1u3ii4yfwpbcx" />
            </motion.div>
          </div>
        </section>

        {/* 6. Step Inside */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7784" data-mp="11fmetavu0l76">
          <SlideAtmosphere videoUrl="https://cdn.magicpatterns.com/uploads/2vbujNeh6XCJnC6Xt6Yz87/animate_this_image_so_its_like.mp4" opacity={1} overlayGradient="dark" lightScrim videoBrightness={1.6} playbackRate={0.5} data-id="element-7785" data-mp="1fbq4cqvrg5db" />
          <PunjabiPlacard word="ਦਰਬਾਰ" translation="Court" data-id="element-7786" data-mp="2lw966lnfrtr3" />

          <div className="absolute bottom-24 left-0 right-0 z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7787" data-mp="1jfp5mmilerki">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2
          }} data-id="element-7788" data-mp="1oe8vgy0b4ni5">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#FDFBF7] leading-tight drop-shadow-[0_10px_30px_rgba(0,0,0,0.8)]" data-id="element-7789" data-mp="10rk7pvoma0tm">
                Step into a world of empire, diplomacy, art, and courage.
              </h2>
            </motion.div>
          </div>
        </section>

        {/* 7. Portraits That Awaken */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7790" data-mp="h2002ok9myn6">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7791" data-mp="m0kz4kns7ca1" />
          <PunjabiPlacard word="ਦਰਸ਼ਨ" translation="Vision" data-id="element-7792" data-mp="lyqe76cifk5y" />

          <div className="relative z-10 max-w-5xl mx-auto w-full px-6 flex flex-col md:flex-row items-center gap-12" data-id="element-7793" data-mp="eg92qul2pvac">
            <motion.div className="flex-1 text-center md:text-left" initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7794" data-mp="9ja727lrhga1">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-6" data-id="element-7795" data-mp="3mx3rl651df5f">
                Historical figures come to life.
              </h2>
              <p className="text-lg text-white/60 font-light mb-8" data-id="element-7796" data-mp="3hw7o2toljv0j">
                Maharaja Ranjit Singh and others awaken on screen to guide
                students through the Darbar.
              </p>

              <div className="flex items-center gap-4" data-id="element-7797" data-mp="38zf7w1mpge1y">
                <button onClick={handleSlide7Play} className="w-14 h-14 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/20 hover:scale-105 transition-all shrink-0" data-id="element-7798" data-mp="33zu7rnvstznn">
                  {slide7AudioPlaying ? <Pause className="w-6 h-6" data-id="element-7799" data-mp="mrhgyc1magvw" /> : <Play className="w-6 h-6 ml-1" data-id="element-7800" data-mp="qp5gjse98xu4" />}
                </button>
                <AnimatePresence data-id="element-7801" data-mp="cxqybns690o4">
                  {slide7AudioPlaying && <motion.div initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -10
                }} className="flex flex-col" data-id="element-7802" data-mp="hr3inlptilb2">
                      <p className="text-[#D4AF37] text-xs tracking-widest uppercase font-light mb-1" data-id="element-7803" data-mp="3bimvrihp0ob7">
                        Now Playing
                      </p>
                      <p className="text-white/80 text-sm font-serif italic" data-id="element-7804" data-mp="36m662juui1w0">
                        Awakening of the Veiled Lioness
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>
              <audio ref={slide7AudioRef} src="https://cdn.magicpatterns.com/uploads/fHMFP9bk9qh56JpvKMc9vM/Awakening_of_the_Veiled_Lioness_(2).mp3" className="hidden" data-id="element-7805" data-mp="3ugpr0f0u3hhg" />
            </motion.div>
            <motion.div className="flex-1 w-full flex justify-center" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7806" data-mp="33d28yrvcrf1">
              <div className="h-[80vh] max-h-[800px] aspect-[9/16] relative p-4 bg-[#1a140f] border-[16px] border-[#8a6b22] shadow-[0_30px_60px_rgba(0,0,0,0.9),inset_0_0_30px_rgba(0,0,0,0.9)] rounded-sm before:absolute before:inset-0 before:border-[2px] before:border-[#D4AF37]/30 before:m-1.5" data-id="element-7807" data-mp="36hty0da3xhfq">
                <div className="w-full h-full relative overflow-hidden shadow-[inset_0_0_50px_rgba(0,0,0,0.9)] bg-black" data-id="element-7808" data-mp="3ajf9bu6ztnnt">
                  <video ref={slide7VideoRef} src="https://cdn.magicpatterns.com/uploads/oAYvh93WVHg7sLuQmvLMws/WhatsApp%20Video%202026-06-08%20at%2009.07.35.mp4" muted playsInline className="w-full h-full object-cover" data-id="element-7809" data-mp="3p7jv571cefat" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 7b. Paintings Become Portals */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7810" data-mp="1zuoswv2kpzpj">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7811" data-mp="1pr9nq527732x" />
          <PunjabiPlacard word="ਕਲਾ" translation="Art" data-id="element-7812" data-mp="1ts3d72vagpxx" />

          <div className="relative z-10 max-w-5xl mx-auto w-full px-6 flex flex-col md:flex-row-reverse items-center gap-12" data-id="element-7813" data-mp="1tto6yq9s44uq">
            <motion.div className="flex-1 text-center md:text-left" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7814" data-mp="1kz9qwlhfcqak">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-6" data-id="element-7815" data-mp="x35gvg7w4jyn">
                Paintings become portals.
              </h2>
              <p className="text-lg text-white/60 font-light mb-8" data-id="element-7816" data-mp="2rflksad7rm0c">
                Static archival art transforms into living, breathing figures
                right before their eyes.
              </p>

              <div className="flex items-center gap-4" data-id="element-7817" data-mp="2u09ss0uqeuzd">
                <button onClick={handleSlide7bPlay} className="w-14 h-14 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/20 hover:scale-105 transition-all shrink-0" data-id="element-7818" data-mp="2txnl817ucfh5">
                  {slide7bAudioPlaying ? <Pause className="w-6 h-6" data-id="element-7819" data-mp="3do93jbpaq24s" /> : <Play className="w-6 h-6 ml-1" data-id="element-7820" data-mp="3dpk6587093ub" />}
                </button>
                <AnimatePresence data-id="element-7821" data-mp="38r0kl8wbpu2h">
                  {slide7bAudioPlaying && <motion.div initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -10
                }} className="flex flex-col" data-id="element-7822" data-mp="33w3f2by962uc">
                      <p className="text-[#D4AF37] text-xs tracking-widest uppercase font-light mb-1" data-id="element-7823" data-mp="7t59onh1z9jd">
                        Now Playing
                      </p>
                      <p className="text-white/80 text-sm font-serif italic" data-id="element-7824" data-mp="hqa0x1pr9v71">
                        Awakening of the Maharaja
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>
              <audio ref={slide7bAudioRef} src="https://cdn.magicpatterns.com/uploads/6aVtpiwFTh4Fdg7GirPpMC/Awakening_of_the_Maharaja.mp3" className="hidden" data-id="element-7825" data-mp="qpexw3lo5gh3" />
            </motion.div>
            <motion.div className="flex-1 w-full flex justify-center" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7826" data-mp="15gnskr4fhs13">
              <div className="h-[80vh] max-h-[800px] aspect-[9/16] relative p-4 bg-[#1a140f] border-[16px] border-[#8a6b22] shadow-[0_30px_60px_rgba(0,0,0,0.9),inset_0_0_30px_rgba(0,0,0,0.9)] rounded-sm before:absolute before:inset-0 before:border-[2px] before:border-[#D4AF37]/30 before:m-1.5" data-id="element-7827" data-mp="226g6icqu5rox">
                <div className="w-full h-full relative overflow-hidden shadow-[inset_0_0_50px_rgba(0,0,0,0.9)] bg-black" data-id="element-7828" data-mp="1o8rjozd7kah8">
                  <video ref={slide7bVideoRef} src="https://cdn.magicpatterns.com/uploads/4rTD4YeXmjgXGTejYDnVus/WhatsApp%20Video%202026-05-21%20at%2016.14.44%20(1).mp4" muted playsInline className="w-full h-full object-cover" data-id="element-7829" data-mp="1ja7yj16fv2px" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 8. History Speaks Back */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7830" data-mp="3c4fzmh9zelx9">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7831" data-mp="3g8faaj8kyqip" />
          <PunjabiPlacard word="ਸੰਵਾਦ" translation="Dialogue" data-id="element-7832" data-mp="3284k7if3hfyl" />

          <div className="relative z-10 max-w-5xl mx-auto w-full px-6 flex flex-col md:flex-row-reverse items-center gap-12" data-id="element-7833" data-mp="376eelqszwcj5">
            <motion.div className="flex-1 text-center md:text-left" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7834" data-mp="3uvqkagp462gp">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#FDFBF7] mb-4 sm:mb-5" data-id="element-7835" data-mp="3q2xfxtxk4w55">
                From watching history to speaking with it.
              </h2>
              <p className="text-base sm:text-lg text-white/60 font-light mb-8" data-id="element-7836" data-mp="mhhxxqd6q7wz">
                Using live avatar technology, students ask questions and receive
                historically informed responses in real time.
              </p>

              {/* Trilingual */}
              <div className="mb-7" data-id="element-7837" data-mp="mcojs121v2n6">
                <div className="flex items-center gap-2 mb-3 justify-center md:justify-start" data-id="element-7838" data-mp="mg2jt2n8qzrg">
                  <Languages className="w-4 h-4 text-[#D4AF37]" data-id="element-7839" data-mp="ieh1v2vunvae" />
                  <p className="text-[#D4AF37] text-[0.7rem] tracking-[0.25em] uppercase" data-id="element-7840" data-mp="de36akbfqdt9">
                    Answers in four languages
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start" data-id="element-7841" data-mp="3mqefspmznn8">
                  {['English', 'ਪੰਜਾਬੀ', 'Français', 'Español'].map(lang => <span key={lang} className="px-3 py-1.5 text-xs sm:text-sm font-light text-[#FDFBF7] border border-[#D4AF37]/30 rounded-full bg-white/5" data-id="element-7842" data-mp="3qs7tx24oflou">
                      {lang}
                    </span>)}
                </div>
              </div>

              {/* AI Guardrails */}
              <div className="mb-7 flex items-start gap-3 text-left border-l-2 border-[#D4AF37]/30 pl-4" data-id="element-7843" data-mp="26c9thowrka19">
                <ShieldCheck className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" data-id="element-7844" data-mp="2a9xqqylxiewb" />
                <div data-id="element-7845" data-mp="2fa28st2hjhon">
                  <p className="text-[#FDFBF7] text-sm font-medium mb-1" data-id="element-7846" data-mp="2f8hdv2c9dwq9">
                    Controlled, responsible AI
                  </p>
                  <p className="text-white/55 font-light text-xs sm:text-sm leading-relaxed" data-id="element-7847" data-mp="1mn7qo2t54uec">
                    Every response is bounded by a curated, historian-verified
                    knowledge base with strict guardrails — keeping answers
                    accurate, age-appropriate, and culturally respectful. No
                    hallucinations, no anachronisms.
                  </p>
                </div>
              </div>

              {/* Technology Partners */}
              <div className="pt-5 border-t border-white/10" data-id="element-7848" data-mp="3td8gnc41dbvj">
                <div className="flex items-center gap-2 mb-3 justify-center md:justify-start" data-id="element-7849" data-mp="3tfl7kerruwkq">
                  <Tv className="w-4 h-4 text-[#D4AF37]" data-id="element-7850" data-mp="3ogrv8po81bm5" />
                  <p className="text-[#D4AF37] text-[0.7rem] tracking-[0.25em] uppercase" data-id="element-7851" data-mp="6a3lunm522ao">
                    Technology Partners
                  </p>
                </div>
                <div className="flex items-center gap-5 justify-center md:justify-start mb-2" data-id="element-7852" data-mp="g6otlgpjlri7">
                  <span className="font-serif text-xl sm:text-2xl tracking-[0.15em] text-[#FDFBF7]" data-id="element-7853" data-mp="b8f15dv7d50a">
                    SAMSUNG
                  </span>
                </div>
                <p className="text-white/45 font-light text-xs leading-relaxed max-w-md mx-auto md:mx-0" data-id="element-7854" data-mp="pyujmkw8hyur">
                  Samsung has offered to support the initiative with their frame
                  displays and immersive screen technology.
                </p>
              </div>
            </motion.div>
            <motion.div className="flex-1 w-full flex justify-center" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7855" data-mp="1dkeyopff6yh4">
              <div className="h-[80vh] max-h-[800px] aspect-[9/16] relative p-4 bg-[#1a140f] border-[16px] border-[#8a6b22] shadow-[0_30px_60px_rgba(0,0,0,0.9),inset_0_0_30px_rgba(0,0,0,0.9)] rounded-sm before:absolute before:inset-0 before:border-[2px] before:border-[#D4AF37]/30 before:m-1.5" data-id="element-7856" data-mp="18kslgfzldi8b">
                <div className="w-full h-full relative overflow-hidden shadow-[inset_0_0_50px_rgba(0,0,0,0.9)] bg-black" data-id="element-7857" data-mp="kw9dy2od99ha">
                  <video src="https://cdn.magicpatterns.com/uploads/tHe5vNNjxpJUtnsEoB8t32/WhatsApp%20Video%202026-05-26%20at%2015.06.40.mp4" playsInline controls className="w-full h-full object-cover" data-id="element-7858" data-mp="pvvpm7ojqhnz" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 9. Living Archive */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7859" data-mp="3416v83oik8gp">
          <div className="absolute inset-0 overflow-hidden" data-id="element-7860" data-mp="3dxa926sq9vjh">
            <AnimatePresence mode="popLayout" data-id="element-7861" data-mp="3dyv19prkl2e6">
              <motion.img key={archiveIndex} src={['https://uploadthingy.s3.us-west-1.amazonaws.com/rru8mxg1f864Swgj1TtDBp/Gemini_Generated_Image_xf598lxf598lxf59.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/2uRFx3az1Ndo6QPmwLotNT/Gemini_Generated_Image_6xdim76xdim76xdi.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/wVBxvpncFWjZ3U7V1C4TbV/Gemini_Generated_Image_cc8os5cc8os5cc8o.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/iav6C11v1iAqRNyzsLVGur/Gemini_Generated_Image_ouvfm7ouvfm7ouvf.png'][archiveIndex]} initial={{
              opacity: 0,
              x: 100
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -100
            }} transition={{
              duration: 1.5,
              ease: 'easeInOut'
            }} className="absolute inset-0 w-full h-full object-cover" data-id="element-7862" data-mp="3iwmqud4qscf7" />
            </AnimatePresence>
          </div>
          <SlideAtmosphere overlayGradient="gold" withTexture data-id="element-7863" data-mp="23xhauremap59" />
          <PunjabiPlacard word="ਯਾਦ" translation="Memory" data-id="element-7864" data-mp="1yz5z2b4ri4ke" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-7865" data-mp="2wfl7065jqfit">
            <motion.div className="text-center" initial={{
            opacity: 0,
            y: -20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} data-id="element-7866" data-mp="2wgwby9aq9tbi">
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] mb-6 drop-shadow-2xl" data-id="element-7867" data-mp="g219r6xj1t32">
                A living archive of memories.
              </h2>
              <p className="text-xl text-white/90 font-light drop-shadow-lg" data-id="element-7868" data-mp="kyj91ra38z80">
                Cinematic scenes of battles, diplomacy, and sacred spaces.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 10. Scent, Sound, Taste */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7869" data-mp="3t9cqg7m25141">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7870" data-mp="2208hqdcojn2" />
          <PunjabiPlacard word="ਅਹਿਸਾਸ" translation="Feeling" data-id="element-7871" data-mp="1rhpxi0iluw4x" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-7872" data-mp="1wfrcoacwubb4">
            <motion.div className="text-center mb-16" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1
          }} data-id="element-7873" data-mp="1wdorc3mpd6cq">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-4" data-id="element-7874" data-mp="3qppuantdqins">
                Not just seen. Felt.
              </h2>
              <p className="text-lg text-white/60 font-light" data-id="element-7875" data-mp="3ie1al5edus4">
                Sound, scent, and taste make history unforgettable.
              </p>
              <p className="text-[#D4AF37]/80 text-xs sm:text-sm tracking-[0.25em] uppercase mt-4" data-id="element-7876" data-mp="3uoyotuw8ukke">
                Every drink poured from ingredients of the era · Every scent
                researched from the period
              </p>
            </motion.div>

            <div className="flex justify-center items-center h-[50vh]" data-id="element-7877" data-mp="2f7oj51jryd1h">
              {[{
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/e7tNmw42aGJMhG2K8pg9sq/Sher-e-Punjab_Lassi_%28Lion_of_Punjab_Smoothie%29.webp',
              name: 'Sher-e-Punjab Lassi',
              inspiredBy: 'Inspired by Maharaja Ranjit Singh',
              scent: 'Cardamom, saffron & sweet cream',
              era: 'Saffron carried over the Khyber, churned yogurt of the court'
            }, {
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7s7gbBG7Kx151cwzpHK1du/Hari_Singh_Nalwa%E2%80%99s_Tiger_Fury_Sharbat.webp',
              name: 'Tiger Fury Sharbat',
              inspiredBy: 'Inspired by Hari Singh Nalwa',
              scent: 'Pomegranate, roasted cumin & ginger',
              era: 'Wild pomegranate & tamarind — the warrior sharbat of the frontier'
            }, {
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/eKo28eax4tv8HfFsN3KZGY/Chabeel_of_Compassion_%28Rose__Kewra_Cooler%29.webp',
              name: 'Chabeel of Compassion',
              inspiredBy: 'Inspired by Moran Sarkar',
              scent: 'Rose petals & kewra blossom',
              era: 'Rose & kewra essence — the chabeel offered in selfless seva'
            }, {
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uNnPb6Ex8V8D1T89ouiXSm/Shahi_Aam_Panna_%28Royal_Raw_Mango_Punch%29.webp',
              name: 'Shahi Aam Panna',
              inspiredBy: 'Inspired by Maharani Jind Kaur',
              scent: 'Raw mango, fresh mint & black salt',
              era: 'Green mango cooler — the summer drink of the zenana'
            }, {
              src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pMQJkiKcDmbaWHaNH1zaMn/Pehalwan%E2%80%99s_Punch_%28Banana_Jaggery_Shake%29.webp',
              name: "Pehalwan's Punch",
              inspiredBy: 'Inspired by Punjabi wrestlers',
              scent: 'Banana, jaggery, cardamom & nutmeg',
              era: 'Banana & jaggery shake with yogurt, honey & almonds — the akhara’s strength fuel'
            }].map((drink, i) => {
              const isCenter = i === drinkIndex;
              const offset = (i - drinkIndex + 5) % 5;
              const position = offset >= 3 ? offset - 5 : offset;
              return <motion.div key={i} animate={{
                x: position * 250,
                scale: isCenter ? 1.2 : 0.8,
                opacity: isCenter ? 1 : 0.4,
                zIndex: isCenter ? 20 : 10
              }} transition={{
                duration: 0.8,
                ease: 'easeInOut'
              }} className="absolute text-center w-48 sm:w-56" data-id="element-7878" data-mp="279lebdsl94yd" data-mpi={i}>
                    {/* Inspired by — above the image */}
                    <motion.p animate={{
                  opacity: isCenter ? 1 : 0
                }} transition={{
                  duration: 0.5
                }} className="absolute bottom-full left-0 right-0 mb-3 text-white/80 text-xs italic font-light drop-shadow-md" data-id="element-7879" data-mp="3ip0e0pc6max6">
                      {drink.inspiredBy}
                    </motion.p>

                    <div className="aspect-[3/4] overflow-hidden border border-[#D4AF37]/40 bg-[#0a0604] relative shadow-2xl" data-id="element-7880" data-mp="o9vn5kl22dja">
                      <img src={drink.src} alt={drink.name} className="w-full h-full object-cover" data-id="element-7881" data-mp="jbu7l9mu8wck" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" data-id="element-7882" data-mp="t6vurzxjzoci" />
                    </div>

                    {/* Name + scent — below the image */}
                    <motion.div animate={{
                  opacity: isCenter ? 1 : 0
                }} transition={{
                  duration: 0.5
                }} className="absolute top-full left-0 right-0 mt-4" data-id="element-7883" data-mp="24pf1shpws667">
                      <p className="text-[#D4AF37] text-sm tracking-widest uppercase mb-2 font-medium drop-shadow-md" data-id="element-7884" data-mp="gpbd40eehn7c">
                        {drink.name}
                      </p>
                      <div className="flex items-center justify-center gap-2 text-white/55 mb-2" data-id="element-7885" data-mp="pq9fyfd3rooc">
                        <Wind className="w-3.5 h-3.5 text-[#D4AF37]/70 shrink-0" data-id="element-7886" data-mp="upcnc6gk26zh" />
                        <p className="text-[0.7rem] font-light tracking-wide drop-shadow-md" data-id="element-7887" data-mp="un9lmp61ipex">
                          {drink.scent}
                        </p>
                      </div>
                      <p className="text-[0.65rem] font-light italic text-white/40 leading-snug max-w-[14rem] mx-auto drop-shadow-md" data-id="element-7888" data-mp="2wijyd3p4y2fk">
                        {drink.era}
                      </p>
                    </motion.div>
                  </motion.div>;
            })}
            </div>
          </div>
        </section>

        {/* 11. In Progress Now */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7889" data-mp="19t5mxbpm0f1n">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7890" data-mp="14v432okd0bp4" />
          <PunjabiPlacard word="ਸਿਰਜਣਾ" translation="Creation" data-id="element-7891" data-mp="21iij4km3q6mm" />

          <div className="relative z-10 max-w-4xl mx-auto px-6 w-full py-8" data-id="element-7892" data-mp="1xhyk1bhywsxu">
            <motion.div className="text-center mb-6" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1
          }} data-id="element-7893" data-mp="1xfu76423cjlf">
              <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-2" data-id="element-7894" data-mp="h1m1dl5jiowh">
                This is no longer just an idea.
              </h2>
              <p className="text-base text-white/60 font-light mb-1" data-id="element-7895" data-mp="nrz8hprf2qgt">
                Testing the environment, scale, and production pathway.
              </p>
              <p className="text-[0.65rem] text-[#D4AF37] tracking-widest uppercase font-light" data-id="element-7896" data-mp="e1xg4ls16lw3">
                Live footage from construction at Illuminarium at the
                Distillery, June 2026.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-4" data-id="element-7897" data-mp="16pa3gy60mxqg">
              {['https://cdn.magicpatterns.com/uploads/a7heywR1FeDYnub9ua9WT9/WhatsApp_Image_2026-06-12_at_14.36.03_(1).jpg', 'https://cdn.magicpatterns.com/uploads/ssJFUVqUnzkDMnb4yqvNvT/WhatsApp_Image_2026-06-13_at_10.33.54.jpg', 'https://cdn.magicpatterns.com/uploads/kQxbd1wr2FNT6U2x9evwFs/WhatsApp_Image_2026-06-12_at_14.36.03_(6).jpg', 'https://cdn.magicpatterns.com/uploads/pnghcT8rVnDqxy4q3pgpBM/WhatsApp_Image_2026-06-12_at_14.36.03_(7).jpg'].map((src, i) => <motion.div key={i} onClick={() => setSelectedImage(src)} initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.8,
              delay: i * 0.1
            }} className="aspect-video bg-[#0a0604] border border-[#D4AF37]/20 relative overflow-hidden cursor-pointer group" data-id="element-7898" data-mp="1s33mwj0mc9kx" data-mpi={i}>
                  <img src={src} alt="Progress" className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700" data-id="element-7899" data-mp="2kqzej9bbzbau" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center" data-id="element-7900" data-mp="qehyomstndg8">
                    <span className="opacity-0 group-hover:opacity-100 text-white/90 text-xs tracking-widest uppercase font-light transition-opacity duration-500" data-id="element-7901" data-mp="n9sett70ozdo">
                      Enlarge
                    </span>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 12. Pathway — Bramalea pilot */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7902" data-mp="16968sb4qya46">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7903" data-mp="126rt1zwdjqh4" />
          <PunjabiPlacard word="ਸਿੱਖਿਆ" translation="Education" data-id="element-7904" data-mp="1fz7yt8gnu2lq" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 items-center gap-12 lg:gap-16" data-id="element-7905" data-mp="1fyg3lyqz66vz">
            {/* Left: the story */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-7906" data-mp="1pygpnsg8b13v">
              <span className="inline-flex items-center gap-2 text-[#D4AF37] text-[0.65rem] tracking-[0.3em] uppercase font-light mb-5" data-id="element-7907" data-mp="28ko66ez6l57t">
                <span className="w-8 h-px bg-[#D4AF37]/60" data-id="element-7908" data-mp="1uo0zhkhff0op" />
                The Pilot
              </span>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] leading-[1.05] mb-6" data-id="element-7909" data-mp="1zmugvnzu3bu5">
                We start where it's
                <br data-id="element-7910" data-mp="1pn3jnejw2iqd" />
                hardest — on purpose.
              </h2>
              <p className="text-lg text-white/70 font-light leading-relaxed mb-6" data-id="element-7911" data-mp="1bvozv5i5qdi9">
                Our first pilot launches at{' '}
                <span className="text-[#D4AF37]" data-id="element-7912" data-mp="1gvl2p1f4byw8">
                  Bramalea Secondary School
                </span>
                , in one of Brampton's least affluent neighbourhoods — the
                students who are too often left out of the story.
              </p>
              <p className="text-lg text-white/70 font-light leading-relaxed mb-8" data-id="element-7913" data-mp="2a7rizc445eh8">
                Prove that heritage can light up a classroom{' '}
                <span className="text-[#FDFBF7]" data-id="element-7914" data-mp="258yfmcijj3us">here</span>, and we prove it
                can work anywhere.
              </p>

              {/* Expansion ladder */}
              <div className="flex items-center gap-3 flex-wrap" data-id="element-7915" data-mp="1vhby5yfm08jl">
                {[{
                label: 'Bramalea',
                sub: '1 school'
              }, {
                label: 'Peel Region',
                sub: 'Regional'
              }, {
                label: 'Canada',
                sub: 'National'
              }].map((step, i) => <div key={step.label} className="flex items-center gap-3" data-id="element-7916" data-mp="3bx52ei1li6bo" data-mpi={i}>
                    <div className={`px-4 py-2 border ${i === 0 ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#D4AF37]/25 bg-black/30'}`} data-id="element-7917" data-mp="36xipk9pois3e">
                      <span className={`block text-sm font-serif ${i === 0 ? 'text-[#D4AF37]' : 'text-white/80'}`} data-id="element-7918" data-mp="3eeypyh03nuha">
                        {step.label}
                      </span>
                      <span className="block text-[0.6rem] tracking-widest uppercase text-white/40 font-light" data-id="element-7919" data-mp="3t5o3vr4y3nou">
                        {step.sub}
                      </span>
                    </div>
                    {i < 2 && <ArrowRight className="w-4 h-4 text-[#D4AF37]/50 flex-shrink-0" data-id="element-7920" data-mp="30i1ozmq9owmo" />}
                  </div>)}
              </div>
            </motion.div>

            {/* Right: the school + quote */}
            <motion.div className="w-full" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1,
            delay: 0.2
          }} data-id="element-7921" data-mp="3ad3fon06y6rd">
              <div className="relative overflow-hidden border border-[#D4AF37]/20 shadow-2xl" data-id="element-7922" data-mp="2gsh974p691x8">
                <img src="https://cdn.magicpatterns.com/uploads/tt3kwmA1Jc3p3kSKo745wm/pasted-image.webp" alt="Bramalea Secondary School, 510 Balmoral Drive, Brampton" className="w-full h-56 sm:h-64 object-cover" data-id="element-7923" data-mp="2urqqk7p4622s" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" data-id="element-7924" data-mp="1y3u5apiqeil8" />
                <div className="absolute bottom-0 left-0 p-4" data-id="element-7925" data-mp="2buzae74vc32y">
                  <p className="text-[#FDFBF7] text-sm font-serif" data-id="element-7926" data-mp="273pkr6jbkzr2">
                    Bramalea Secondary School
                  </p>
                  <p className="text-white/50 text-[0.65rem] tracking-widest uppercase font-light" data-id="element-7927" data-mp="1jck5ashak9ha">
                    Brampton, Ontario
                  </p>
                </div>
              </div>

              <div className="border-l-[2px] border-[#D4AF37]/50 pl-6 mt-8 relative" data-id="element-7928" data-mp="157kzkalhbwdx">
                <div className="absolute -left-[1px] top-0 w-[2px] h-10 bg-[#D4AF37]" data-id="element-7929" data-mp="1a04lbuublxtt" />
                <p className="font-serif text-xl sm:text-2xl text-[#D4AF37] mb-4 leading-relaxed italic" data-id="element-7930" data-mp="vaydxjlbx1au">
                  "Our community does not get enough space in historical
                  education. This is a powerful opportunity."
                </p>
                <p className="text-white/40 text-[0.65rem] tracking-widest uppercase font-light" data-id="element-7931" data-mp="1nwzynzhlubfo">
                  — Principal, Bramalea Secondary School
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 13. Treasury Cards Fan */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7932" data-mp="1j1ucr0o7cwrl">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7933" data-mp="7kks4atc7yop" />
          <PunjabiPlacard word="ਖ਼ਜ਼ਾਨਾ" translation="Treasury" data-id="element-7934" data-mp="7lvy8ha99iiz" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full flex flex-col items-center justify-center h-full py-4" data-id="element-7935" data-mp="3l0h8qa2h1ooi">
            <motion.div className="text-center mb-2" initial={{
            opacity: 0,
            y: -20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} data-id="element-7936" data-mp="3fyrz8yuwdj2j">
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-none mb-2 tracking-wide uppercase" data-id="element-7937" data-mp="2lq9j8xebdwd">
                Treasures of the{' '}
                <span className="text-[#D4AF37]" data-id="element-7938" data-mp="3ty1f1xf6yb5z">Sikh Empire</span>
              </h2>
              <p className="text-xs sm:text-sm text-white/60 tracking-[0.2em] uppercase font-light" data-id="element-7939" data-mp="3pyafb47oskpb">
                Recovering the lost stories of the Sikh Kingdoms.
              </p>
            </motion.div>

            <TreasuryFan cards={[{
            src: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png',
            name: 'Hari Singh Nalwa',
            desc: 'The most feared general of the Sikh Khalsa Army, Hari Singh Nalwa pushed the empire to the Khyber Pass — the first commander in centuries to subdue the Afghan frontier. His name alone was said to quiet restless children across the borderlands. He fell at Jamrud in 1837, defending the gateway he had won.',
            stats: [{
              label: 'Rarity',
              value: 'Legendary'
            }, {
              label: 'Type',
              value: 'Figure'
            }]
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/st6W3BtxYpFsMW4u23CAxv/Harmandir_Sahib_Amritsar.png',
            name: 'Harmandir Sahib',
            desc: 'The golden spiritual heart of the Sikh faith, rising from the sacred Amritsar pool. Maharaja Ranjit Singh gilded its upper floors in gold and marble, giving it the name the world knows: the Golden Temple. Open on all four sides, it welcomes every soul, of every faith, to its langar and its light.',
            stats: [{
              label: 'Rarity',
              value: 'Mythic'
            }, {
              label: 'Type',
              value: 'Place'
            }]
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/jyrMYyNM9JskmMzEnBrLTw/Koh-i-noor_Armlet.png',
            name: 'Koh-i-Noor Armlet',
            desc: 'The Koh-i-Noor — “Mountain of Light” — the most storied diamond on earth, worn by Ranjit Singh strapped to his upper arm in this jeweled armlet. After his death the British seized it as a spoil of annexation and set it into the Crown Jewels, where it remains a symbol of all that was taken.',
            stats: [{
              label: 'Rarity',
              value: 'Mythic'
            }, {
              label: 'Type',
              value: 'Ornament'
            }]
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/itiARdFbZcdkmdrUEtYH65/Golden_Throne_of_Lahore.png',
            name: 'Golden Throne of Lahore',
            desc: 'The hexagonal throne of Maharaja Ranjit Singh, crafted by goldsmith Hafez Muhammad Multani from wood and resin sheathed in pure gold. Its lotus-petal base lifted the Lion of Punjab above his court. Carried off after the empire fell, it now sits behind glass in a London museum, far from the Lahore Darbar it once crowned.',
            stats: [{
              label: 'Rarity',
              value: 'Legendary'
            }, {
              label: 'Type',
              value: 'Artifact'
            }]
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png',
            name: 'Ranjit Singh',
            desc: 'The Sher-e-Punjab — the Lion of Punjab — who united the warring misls into a single sovereign empire by 1801. Blind in one eye and unable to read, he forged a court where Sikhs, Muslims, and Hindus rose on merit alone. For forty years no foreign power dared cross the Sutlej while he reigned.',
            stats: [{
              label: 'Rarity',
              value: 'Legendary'
            }, {
              label: 'Type',
              value: 'Ruler'
            }]
          }]} data-id="element-7940" data-mp="li7wze57ormt" />

            <motion.div className="text-center mt-10 max-w-3xl mx-auto" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.3
          }} data-id="element-7941" data-mp="1rcrp9i8304td">
              <div className="flex items-center justify-center gap-4 mb-3" data-id="element-7942" data-mp="18kytxh5ji7ih">
                <div className="w-12 h-px bg-[#D4AF37]/30" data-id="element-7943" data-mp="13lw0ktyx9pqc" />
                <Star className="w-4 h-4 text-[#D4AF37]" data-id="element-7944" data-mp="13mf6fcbasno0" />
                <div className="w-12 h-px bg-[#D4AF37]/30" data-id="element-7945" data-mp="ynu85iflwe27" />
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7] mb-2" data-id="element-7946" data-mp="tnpzgekxwnmj">
                Real History.{' '}
                <span className="text-[#D4AF37]" data-id="element-7947" data-mp="pm4h4dpmzh4y">Real Connection.</span>
              </h3>
              <p className="text-base sm:text-lg text-white/80 font-serif italic leading-relaxed" data-id="element-7948" data-mp="1ecikc55ytsn6">
                "A digital bridge to the physical past. Every artifact and
                figure is <span className="text-[#D4AF37]" data-id="element-7949" data-mp="15kyoprwmjywf">real history</span>.
                You aren't just playing a game — you are{' '}
                <span className="text-[#D4AF37]" data-id="element-7950" data-mp="19l8vv7w5kvcv">recovering a lost legacy</span>{' '}
                and building a modern Sangat."
              </p>
            </motion.div>
          </div>
        </section>

        {/* 13b. Daily Packet */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7951" data-mp="10jj4u366nyfm">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7952" data-mp="qng513yt0bvu" />
          <PunjabiPlacard word="ਰੋਜ਼ਾਨਾ" translation="Daily" data-id="element-7953" data-mp="qnpvesnrgl22" />

          <div className="relative z-10 max-w-4xl mx-auto px-6 w-full flex flex-col items-center" data-id="element-7954" data-mp="2gzm1eh0osfgk">
            <motion.div className="text-center mb-16" initial={{
            opacity: 0,
            y: -20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} data-id="element-7955" data-mp="2l1qp6z4gehva">
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] mb-6 tracking-wide uppercase" data-id="element-7956" data-mp="j602or4tozfb">
                The Daily <span className="text-[#D4AF37]" data-id="element-7957" data-mp="t230jksn6bwg">Packet</span>
              </h2>
              <p className="text-lg text-white/70 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-7958" data-mp="t47fqyvzqxc1">
                Every 24 hours, a sealed leather envelope arrives from the
                Darbar. Open it to reveal new artifacts, and answer the optional
                Court Question for bonus influence.
              </p>
            </motion.div>

            <div className="mb-16 relative h-64 flex items-center justify-center" data-id="element-7959" data-mp="33tcptz9yuwhr">
              <DailyPacketAnimation data-id="element-7960" data-mp="2yvkx531hz6ci" />
            </div>

            <motion.div className="text-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.2
          }} data-id="element-7961" data-mp="2ywvzcyfao61i">
              <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-7962" data-mp="1gsqwkqfp64nn">
                Pokémon meets Magic: The Gathering meets Sikh history.
              </h3>
              <p className="text-lg text-white/60 font-light" data-id="element-7963" data-mp="1qpc4pkn0jvvp">
                Students learn by collecting, unlocking, and competing.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 14. School to Show */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7964" data-mp="20f5i8r6pul2l">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7965" data-mp="24f7a68mng04o" />
          <PunjabiPlacard word="ਸਫ਼ਰ" translation="Journey" data-id="element-7966" data-mp="29h4znkln7r62" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-7967" data-mp="3ty5nuwvl0p5c">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-7968" data-mp="3tw1fo2qnh3z9">
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] leading-tight mb-12" data-id="element-7969" data-mp="gj9gq2m0p9zu">
                First they unlock the story.
                <br data-id="element-7970" data-mp="cj7b5id5vwfa" />
                <span className="text-[#D4AF37]" data-id="element-7971" data-mp="ck0myxqz9c4a">
                  Then they step inside it.
                </span>
              </h2>
              <div className="flex flex-wrap items-center justify-center gap-4 text-xs tracking-[0.2em] uppercase font-light text-white/50" data-id="element-7972" data-mp="2scmy8gcumr7">
                <span data-id="element-7973" data-mp="108a0zdhy8vkv">School</span>
                <span className="text-[#D4AF37] opacity-50" data-id="element-7974" data-mp="109bhhxhqia9z">→</span>
                <span data-id="element-7975" data-mp="vf78z9stlu6s">Cards</span>
                <span className="text-[#D4AF37] opacity-50" data-id="element-7976" data-mp="vexill3v5l0k">→</span>
                <span data-id="element-7977" data-mp="3rm81lihab9c">Bus</span>
                <span className="text-[#D4AF37] opacity-50" data-id="element-7978" data-mp="8jxaav0oy952">→</span>
                <span className="text-[#D4AF37]" data-id="element-7979" data-mp="8l8e2uu4zgw6">Immersive Show</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Sneak Peek (moved after School to Show) */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-7980" data-mp="3uzrt8tqxr03p">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-7981" data-mp="mhvoo0f0i5kk" />
          <PunjabiPlacard word="ਝਲਕ" translation="Glimpse" data-id="element-7982" data-mp="ri9l0l793r2r" />

          <div className="relative z-10 max-w-5xl mx-auto w-full px-6 text-center" data-id="element-7983" data-mp="djjnj4girpdv">
            <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1
          }} data-id="element-7984" data-mp="hkls80t7gtn8">
              <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-8" data-id="element-7985" data-mp="32ce25c9x4yri">
                A glimpse of the experience.
              </h2>
              <div className="aspect-video bg-black border border-[#D4AF37]/20 shadow-[0_0_50px_rgba(212,175,55,0.1)] relative p-1" data-id="element-7986" data-mp="2psh2izl0cn9a">
                <iframe src="https://www.youtube.com/embed/pW6rFd_Td0o?controls=0&rel=0&modestbranding=1&showinfo=0" title="Sneak Peek" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen" allowFullScreen className="w-full h-full" data-id="element-7987" data-mp="34o6iafzlo45k" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* 17b. Hari Singh Nalwa — the story is already travelling */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-12" data-id="element-7988" data-mp="1tacfh0psmt7s">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-7989" data-mp="1oc17ywmz0usq" />
          <PunjabiPlacard word="ਦੁਨੀਆ" translation="The World" data-id="element-7990" data-mp="1o9exao4s9z6a" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full grid lg:grid-cols-[0.82fr_1.18fr] gap-10 lg:gap-14 items-center" data-id="element-7991" data-mp="1xi0gyb5retzr">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.28,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-7992" data-mp="279ekqv4qufyp">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-5" data-id="element-7993" data-mp="1elif30x75dps">
                Hari Singh Nalwa · American Mainstream
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-[1.05] mb-6" data-id="element-7994" data-mp="1oin55bu5xtbv">
                The world is already{' '}
                <span className="text-[#D4AF37]" data-id="element-7995" data-mp="1ji8wpsonghdb">ready for this story.</span>
              </h2>
              <p className="text-white/65 font-light text-base sm:text-lg leading-relaxed mb-7" data-id="element-7996" data-mp="2zwij4h9bzw1i">
                Hari Singh Nalwa already stands inside the visual mythology of
                the Dallas Cowboys, on their Carpe Omnia installation and
                apparel. A Sikh hero has entered one of America&rsquo;s biggest
                cultural stages.
              </p>
              <div className="border-l-2 border-[#D4AF37] pl-5" data-id="element-7997" data-mp="2tiv5yl8r4zfh">
                <p className="font-serif text-xl sm:text-2xl text-[#FDFBF7] leading-snug" data-id="element-7998" data-mp="2xj59lr92ndr6">
                  Other communities have carried their heroes into the
                  mainstream. Why are we not telling the world about the
                  kingdoms we built?
                </p>
              </div>
              <p className="mt-7 text-sm text-white/45 font-light leading-relaxed" data-id="element-7999" data-mp="3ch7ec4rua5a0">
                Moana showed what happens when a community&rsquo;s story is given
                global scale. Our history is ready for the same cultural
                confidence, told by us and owned by us.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 18
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.28,
            delay: 0.06
          }} className="grid grid-cols-2 gap-3" data-id="element-8000" data-mp="cuqyxf5q71q4">
              <button type="button" onClick={() => setSelectedImage('https://cdn.magicpatterns.com/uploads/eebyzsoCzGYj6KQ5L44Zvs/images_(1).jpg')} className="col-span-2 overflow-hidden border border-[#D4AF37]/35 bg-[#302d4d] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" aria-label="Enlarge the Carpe Omnia Hari Singh Nalwa artwork" data-id="element-8001" data-mp="dnf89bn3d07r">
                <img src="https://cdn.magicpatterns.com/uploads/eebyzsoCzGYj6KQ5L44Zvs/images_(1).jpg" alt="Carpe Omnia artwork featuring Hari Singh Nalwa with the words Seize Everything" className="h-64 sm:h-72 w-full object-contain" data-id="element-8002" data-mp="3m5uudkkr6h4e" />
              </button>
              <button type="button" onClick={() => setSelectedImage('https://cdn.magicpatterns.com/uploads/uxDwhMBewnGnvtdwy5Lwf1/dallas.jpg')} className="overflow-hidden border border-white/15 bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" aria-label="Enlarge the Dallas Cowboys installation in its venue" data-id="element-8003" data-mp="324jiceyh3bph">
                <img src="https://cdn.magicpatterns.com/uploads/uxDwhMBewnGnvtdwy5Lwf1/dallas.jpg" alt="Carpe Omnia installation displayed inside the Dallas Cowboys venue" className="h-44 sm:h-52 w-full object-contain" data-id="element-8004" data-mp="2o8o7mwe896x6" />
              </button>
              <button type="button" onClick={() => setSelectedImage('https://cdn.magicpatterns.com/uploads/2S8vwWQpHyr3wEHDs7c4Gi/dallas-cowboys_480x480.webp')} className="overflow-hidden border border-white/15 bg-[#23213b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" aria-label="Enlarge the Dallas Cowboys Carpe Omnia apparel" data-id="element-8005" data-mp="25katc3ox6sqc">
                <img src="https://cdn.magicpatterns.com/uploads/2S8vwWQpHyr3wEHDs7c4Gi/dallas-cowboys_480x480.webp" alt="Dallas Cowboys player wearing Carpe Omnia apparel featuring Hari Singh Nalwa" className="h-44 sm:h-52 w-full object-contain" data-id="element-8006" data-mp="tua9uygp1prn" />
              </button>
            </motion.div>
          </div>
        </section>

        {/* Appendix · Access Problem */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" style={{
        order: 2
      }} data-id="element-8007" data-mp="35kw3o1yo6m37">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-8008" data-mp="1g2fraor9b1hb" />
          <PunjabiPlacard word="ਪਹੁੰਚ" translation="Access" data-id="element-8009" data-mp="1g5bno8nghi3n" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-8010" data-mp="1l0r0qydn39z0">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2
          }} data-id="element-8011" data-mp="16dxueim3leiy">
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] leading-tight mb-8" data-id="element-8012" data-mp="12budhegnthqa">
                The barrier should not be a bus.
              </h2>
              <p className="text-lg text-white/60 font-light max-w-2xl mx-auto" data-id="element-8013" data-mp="sdepvlgucqlj">
                If we want this to reach the next generation, we must remove the
                financial barriers to transportation and access.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Appendix · First Year Goal */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" style={{
        order: 2
      }} data-id="element-8014" data-mp="2lb1st2p1rmcc">
          <SlideAtmosphere overlayGradient="gold" withTexture data-id="element-8015" data-mp="2bbja0pkvi6lv" />
          <PunjabiPlacard word="ਨਿਸ਼ਾਨਾ" translation="Target" data-id="element-8016" data-mp="2be5jiurbqw6q" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-8017" data-mp="27bsnwz6akeno">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.5
          }} data-id="element-8018" data-mp="340zyxd9ab5dc">
              <h2 className="font-serif text-7xl sm:text-8xl text-[#FDFBF7] leading-none mb-4" data-id="element-8019" data-mp="2q8af9fh8acje">
                12,000
              </h2>
              <p className="text-xl tracking-[0.3em] uppercase text-[#D4AF37] font-light mb-8" data-id="element-8020" data-mp="3mtcdcyd7px3u">
                Students in Year One
              </p>
              <div className="w-px h-12 bg-gradient-to-b from-[#D4AF37]/50 to-transparent mx-auto" data-id="element-8021" data-mp="14in7q8zkyw96" />
            </motion.div>
          </div>
        </section>

        {/* Appendix · The Ask — Sponsor a Bus */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-12" style={{
        order: 2
      }} data-id="element-8022" data-mp="1dhia19zi7m77">
          <SlideAtmosphere overlayGradient="gold" withTexture data-id="element-8023" data-mp="1nh99lq2x8re5" />
          <PunjabiPlacard word="ਸੇਵਾ" translation="Service" data-id="element-8024" data-mp="1newsff4zml25" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 w-full" data-id="element-8025" data-mp="1sej43k563t8u">
            {/* Header */}
            <motion.div className="text-center mb-10 sm:mb-12" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-8026" data-mp="1x7u16tk6gvnk">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-8027" data-mp="1m59bv5r31dwd">
                The Ask
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-[1.1] mb-5" data-id="element-8028" data-mp="1v3ky6vkx1pj0">
                Sponsor a bus. <br className="hidden sm:block" data-id="element-8029" data-mp="1r1zqrpr5i3g7" />
                <span className="text-[#D4AF37]" data-id="element-8030" data-mp="1r051jz8ujp6b">
                  Carry a generation forward.
                </span>
              </h2>
              <p className="text-base sm:text-lg text-white/65 font-light max-w-2xl mx-auto" data-id="element-8031" data-mp="ye3uun9eofkh">
                In partnership with the{' '}
                <span className="text-[#FDFBF7]" data-id="element-8032" data-mp="1c6jqh2zz9bbd">
                  Sikh Foundation of Canada
                </span>
                , donors fund buses that bring students across the GTA to the
                experience — and receive an official tax receipt.
              </p>
            </motion.div>

            {/* Sponsorship options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8" data-id="element-8033" data-mp="5jfivvqn989z">
              {/* Bus Sponsor — hero */}
              <motion.div className="bg-black/40 border border-[#D4AF37]/40 rounded-2xl p-7 backdrop-blur-md relative overflow-hidden shadow-[0_0_40px_rgba(212,175,55,0.12)]" initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} viewport={{
              once: true
            }} data-id="element-8034" data-mp="aae3h7io0sj1">
                <div className="absolute top-0 left-0 w-full h-1 bg-[#D4AF37]" data-id="element-8035" data-mp="j9rcjz2vnf38" />
                <Bus className="w-8 h-8 text-[#D4AF37] mb-4" data-id="element-8036" data-mp="2z1yfccu9o5jp" />
                <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-8037" data-mp="2z0dkem41iklb">
                  Sponsor a Bus
                </h3>
                <p className="text-white/60 font-light text-sm leading-relaxed mb-4" data-id="element-8038" data-mp="21kj2bx9cn5gd">
                  Fund transportation for a full classroom of students —
                  removing the single biggest barrier to access.
                </p>
                <div className="flex items-center gap-2 text-[#D4AF37] text-xs tracking-widest uppercase" data-id="element-8039" data-mp="2am8oooodje7a">
                  <Receipt className="w-4 h-4" data-id="element-8040" data-mp="2fiz9fpb4ixz1" />
                  Tax receipt provided
                </div>
              </motion.div>

              {/* Other sponsors */}
              <motion.div className="bg-black/30 border border-white/10 rounded-2xl p-7 backdrop-blur-md relative overflow-hidden" initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.8,
              delay: 0.35
            }} viewport={{
              once: true
            }} data-id="element-8041" data-mp="1muuzgrliryqj">
                <div className="absolute top-0 left-0 w-full h-1 bg-white/20" data-id="element-8042" data-mp="wj9poh5x78tz" />
                <HeartHandshake className="w-8 h-8 text-[#D4AF37] mb-4" data-id="element-8043" data-mp="rnatw8i9iojj" />
                <h3 className="font-serif text-2xl text-[#FDFBF7] mb-2" data-id="element-8044" data-mp="mmetw6117mje">
                  Become a Sponsor
                </h3>
                <p className="text-white/60 font-light text-sm leading-relaxed mb-4" data-id="element-8045" data-mp="ho3k1vaqlc16">
                  Underwrite the experience, technology, or a school cohort.
                  Flexible sponsorships available for partners who want to do
                  more.
                </p>
                <div className="flex items-center gap-2 text-[#D4AF37] text-xs tracking-widest uppercase" data-id="element-8046" data-mp="3xghtt5rp40c">
                  <Receipt className="w-4 h-4" data-id="element-8047" data-mp="3v52u2a6jyest" />
                  Tax receipt provided
                </div>
              </motion.div>
            </div>

            {/* Foundation reassurance */}
            <motion.p className="text-center text-white/45 font-light text-xs sm:text-sm max-w-2xl mx-auto" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1,
            delay: 0.5
          }} viewport={{
            once: true
          }} data-id="element-8048" data-mp="3m338d4klemjj">
              All donations are processed through the Sikh Foundation of Canada
              — a registered charity promoting Sikh values, history, and
              heritage, and empowering Sikh-Canadian youth.
            </motion.p>
          </div>
        </section>

        {/* Appendix · The Perfect Partner — Sikh Foundation of Canada */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-12" style={{
        order: 2
      }} data-id="element-8049" data-mp="3qd5tbfhmcuis">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-8050" data-mp="mqoxx0srd7pq" />
          <PunjabiPlacard word="ਸਾਂਝ" translation="Partnership" data-id="element-8051" data-mp="w479y9osfrup" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-8052" data-mp="w2mbwa39lqs3">
            {/* Header */}
            <motion.div className="text-center mb-10 sm:mb-12" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-8053" data-mp="s0revhsd9zk4">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-8054" data-mp="n3r8t6viowt0">
                How Do We Connect?
              </p>
              <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-[1.1] mb-5" data-id="element-8055" data-mp="i3mnn3jnzdwg">
                The Sikh Foundation of Canada <br className="hidden sm:block" data-id="element-8056" data-mp="1yl0pjln9haqm" />
                <span className="text-[#D4AF37]" data-id="element-8057" data-mp="1tkmjua94ucvr">is the perfect partner.</span>
              </h2>
              <p className="text-base sm:text-lg text-white/65 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-8058" data-mp="33iy4b0wgsel">
                They didn't just attend the legacy — they{' '}
                <span className="text-[#FDFBF7]" data-id="element-8059" data-mp="d15d0xi78jyk">created it.</span> As the
                original producers of{' '}
                <span className="text-[#FDFBF7] italic" data-id="element-8060" data-mp="h30fw6fs35eg">
                  Arts of the Sikh Kingdoms
                </span>{' '}
                at the ROM, their mission and ours are one continuous thread.
              </p>
            </motion.div>

            {/* Mission pillars */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10" data-id="element-8061" data-mp="vrn4kyrap9ic">
              {[{
              icon: GraduationCap,
              title: 'Education & Mentorship',
              detail: 'University scholarships for exceptional Sikh-Canadian students, plus the Nurturing Excellence mentorship program.'
            }, {
              icon: Palette,
              title: 'Art & Heritage',
              detail: 'Books, publications, and exhibitions — including the dedicated Gallery of Sikh Art & Culture at the ROM.'
            }, {
              icon: HeartHandshake,
              title: 'Community Building',
              detail: 'Celebrating equality, seva, and compassion through events like the annual Sikh Centennial Gala.'
            }].map((c, i) => <motion.div key={c.title} className="bg-black/30 border border-[#D4AF37]/15 rounded-2xl p-6 backdrop-blur-sm" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.15 + i * 0.1
            }} viewport={{
              once: true
            }} data-id="element-8062" data-mp="38ymnaly76wto" data-mpi={i}>
                  <c.icon className="w-8 h-8 text-[#D4AF37] mb-4" data-id="element-8063" data-mp="jp0qs0w8n186" />
                  <h3 className="font-serif text-xl text-[#FDFBF7] mb-2 leading-tight" data-id="element-8064" data-mp="5uqckdhvapgd">
                    {c.title}
                  </h3>
                  <p className="text-white/55 font-light text-sm leading-relaxed" data-id="element-8065" data-mp="9uqp1sleoqks">
                    {c.detail}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 17b. Why Ash Singh */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-8066" data-mp="3j3may37m5lxs">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-8067" data-mp="3n5pqp41bfcov" />
          <PunjabiPlacard word="ਭਰੋਸਾ" translation="Trust" data-id="element-8068" data-mp="1hbztas9m33dz" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 w-full py-8" data-id="element-8069" data-mp="1lc07qcwpnel1">
            {/* Name + thesis */}
            <motion.div className="text-center mb-10 sm:mb-12" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-8070" data-mp="35tswzfj0n2hz">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-8071" data-mp="3pgyx3ebi2zzz">
                Why Me
              </p>
              <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-[1.1] mb-4" data-id="element-8072" data-mp="3klb8b00xk8v0">
                A lifetime of small steps <br className="hidden sm:block" data-id="element-8073" data-mp="3tk52kjqsgn5e" />
                <span className="text-[#D4AF37]" data-id="element-8074" data-mp="3tkevaf37x8es">that led me here.</span>
              </h2>
              <p className="text-base sm:text-lg text-white/60 font-light max-w-xl mx-auto" data-id="element-8075" data-mp="245h13djqkpm5">
                I've spent decades in service of Sikh education, technology, and
                culture. <span className="text-[#FDFBF7]" data-id="element-8076" data-mp="1e94zx1gg7o1z">— Ash Singh</span>
              </p>
            </motion.div>

            {/* Credential grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-8077" data-mp="1i9f366cuw0d5">
              {[{
              icon: Globe,
              title: '65M+ Reached',
              detail: 'Founder of SikhiWiki & Turbanizer'
            }, {
              icon: GraduationCap,
              title: 'Global Professor',
              detail: 'INSEAD · Ivey · Wharton'
            }, {
              icon: Landmark,
              title: 'Family Legacy',
              detail: 'Arts of the Sikh Kingdoms'
            }, {
              icon: HandCoins,
              title: 'Sevadar',
              detail: 'Sikh Art & Culture Gallery, ROM'
            }, {
              icon: Users,
              title: 'Experience Producer',
              detail: 'Large-scale community events & TV'
            }, {
              icon: Sparkles,
              title: 'Builder',
              detail: 'Startup founder, coder & AI builder'
            }].map((c, i) => <motion.div key={c.title} className="bg-black/30 border border-[#D4AF37]/15 rounded-xl p-5 backdrop-blur-sm flex items-start gap-4" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1 + i * 0.08
            }} viewport={{
              once: true
            }} data-id="element-8078" data-mp="3qivd395ty33x" data-mpi={i}>
                  <c.icon className="w-6 h-6 text-[#D4AF37] shrink-0 mt-0.5" data-id="element-8079" data-mp="2ncb9v5poyjv8" />
                  <div data-id="element-8080" data-mp="2nefbfh04b6sc">
                    <p className="text-[#FDFBF7] font-serif text-lg leading-tight mb-1" data-id="element-8081" data-mp="2sbe37swb71nd">
                      {c.title}
                    </p>
                    <p className="text-white/55 font-light text-xs sm:text-sm leading-snug" data-id="element-8082" data-mp="2weasu3k9ny0t">
                      {c.detail}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Appendix · Q&A: ROM vs Immersive */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-12" style={{
        order: 2
      }} data-id="element-8083" data-mp="28nydw8qlt10q">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-8084" data-mp="2ie9ouf8pgah4" />
          <PunjabiPlacard word="ਸਵਾਲ" translation="Questions" data-id="element-8085" data-mp="1ky3ypkitb0db" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 w-full" data-id="element-8086" data-mp="1kxkjibkjqq2z">
            <motion.div className="text-center mb-8 sm:mb-10" initial={{
            opacity: 0,
            y: -20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-8087" data-mp="1pvcj7rrfnghq">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-3" data-id="element-8088" data-mp="14fpv2x5s1uhw">
                Not Competition
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#FDFBF7] leading-tight max-w-4xl mx-auto mb-4" data-id="element-8089" data-mp="unbmbkga7nm8">
                Two Halves of{' '}
                <span className="text-[#D4AF37]" data-id="element-8090" data-mp="pmnvozvyt8sj">One Journey</span>
              </h2>
              <p className="text-white/70 font-light text-sm sm:text-base max-w-2xl mx-auto leading-relaxed" data-id="element-8091" data-mp="krgzed50z6dn">
                Same audience. Different role in bringing a generation to Sikh
                heritage.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1.25fr] gap-5 md:gap-4 items-center mb-8" data-id="element-8092" data-mp="213tvx1si5o4">
              {/* Doorway Column — the entry point (left) */}
              <motion.div className="bg-black/30 border border-[#D4AF37]/20 rounded-2xl p-6 sm:p-7 backdrop-blur-md relative overflow-hidden" initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 1,
              delay: 0.2
            }} viewport={{
              once: true
            }} data-id="element-8093" data-mp="3o8jznrrnbpeg">
                <div className="absolute top-0 left-0 w-full h-1 bg-[#D4AF37]/40" data-id="element-8094" data-mp="fzabs3qcfv25" />
                <span className="text-[#D4AF37]/60 text-[0.65rem] tracking-[0.3em] uppercase block mb-2" data-id="element-8095" data-mp="13p4licfd93i6">
                  The Doorway
                </span>
                <h3 className="font-serif text-xl sm:text-2xl text-[#D4AF37] mb-4 pb-4 border-b border-[#D4AF37]/15" data-id="element-8096" data-mp="2t7b73ztwucxb">
                  Immersive Exhibit
                </h3>
                <ul className="space-y-3 text-sm text-white/75 font-light" data-id="element-8097" data-mp="2o6x7952gq9ad">
                  <li className="flex items-start gap-3" data-id="element-8098" data-mp="31zd39lwy571s">
                    <span className="text-[#D4AF37]/50 mt-1 shrink-0" data-id="element-8099" data-mp="2xyb4t7ausz0v">•</span>
                    <span data-id="element-8100" data-mp="2a8p4uhmnqfrt">Experiential &amp; national in reach</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-8101" data-mp="259vtovuuf0ut">
                    <span className="text-[#D4AF37]/50 mt-1 shrink-0" data-id="element-8102" data-mp="25bgn2i5f8dr3">•</span>
                    <span data-id="element-8103" data-mp="2japzr7uf4pqb">Activates sponsors &amp; ticket buyers</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-8104" data-mp="2e9itxebu60ma">
                    <span className="text-[#D4AF37]/50 mt-1 shrink-0" data-id="element-8105" data-mp="3uotuig8tyhwf">•</span>
                    <span data-id="element-8106" data-mp="3umzbj7h4czuz">The on-ramp — where the journey begins</span>
                  </li>
                </ul>
              </motion.div>

              {/* Connector arrow */}
              <motion.div className="flex md:flex-col items-center justify-center text-[#D4AF37]" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} transition={{
              duration: 0.8,
              delay: 0.45
            }} viewport={{
              once: true
            }} data-id="element-8107" data-mp="ovt6kteun97p">
                <div className="w-10 h-10 rounded-full bg-[#0a0604] border border-[#D4AF37]/50 flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.25)]" data-id="element-8108" data-mp="1crowblpxl7ye">
                  <ArrowRight className="w-5 h-5 text-[#D4AF37] rotate-90 md:rotate-0" data-id="element-8109" data-mp="1mia1xt40vcqt" />
                </div>
              </motion.div>

              {/* Home Column — the destination (right, emphasized) */}
              <motion.div className="bg-black/50 border-2 border-[#D4AF37]/60 rounded-2xl p-7 sm:p-9 backdrop-blur-md relative overflow-hidden shadow-[0_0_60px_rgba(212,175,55,0.18)]" initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 1,
              delay: 0.5
            }} viewport={{
              once: true
            }} data-id="element-8110" data-mp="1viop6y0jr1wy">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-[#D4AF37]" data-id="element-8111" data-mp="3upddx9aueuiw" />
                <div className="flex items-center gap-3 mb-2" data-id="element-8112" data-mp="3pywwzf7tcqrs">
                  <Landmark className="w-6 h-6 text-[#D4AF37] shrink-0" data-id="element-8113" data-mp="3pxc5jygspny5" />
                  <span className="text-[#D4AF37] text-[0.7rem] tracking-[0.3em] uppercase" data-id="element-8114" data-mp="3kzajr5rzjaiz">
                    The Home
                  </span>
                </div>
                <h3 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-5 pb-5 border-b border-[#D4AF37]/25" data-id="element-8115" data-mp="hiv3l8hd09zj">
                  ROM Gallery
                </h3>
                <ul className="space-y-4 text-base sm:text-lg text-white/85 font-light" data-id="element-8116" data-mp="2btl4bxa8omll">
                  <li className="flex items-start gap-3" data-id="element-8117" data-mp="2fuwvi78rhfvw">
                    <span className="text-[#D4AF37]/70 mt-1.5 shrink-0" data-id="element-8118" data-mp="220cokohyoiup">•</span>
                    <span data-id="element-8119" data-mp="26zyz2q6enkzt">Permanent &amp; scholarly</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-8120" data-mp="1s380gj4k2db9">
                    <span className="text-[#D4AF37]/70 mt-1.5 shrink-0" data-id="element-8121" data-mp="1s1ojeqb2ry8f">•</span>
                    <span data-id="element-8122" data-mp="1x3dsi0eqm1tv">Earns lifelong legacy patrons</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-8123" data-mp="1j3bg2tp780m0">
                    <span className="text-[#D4AF37]/70 mt-1.5 shrink-0" data-id="element-8124" data-mp="1j4wb0kffdlke">•</span>
                    <span data-id="element-8125" data-mp="1o1wimzrxawdm">The destination — where the journey arrives</span>
                  </li>
                </ul>
              </motion.div>
            </div>

            <motion.div className="text-center bg-white/5 border border-white/10 rounded-xl p-6 max-w-3xl mx-auto" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.6
          }} viewport={{
            once: true
          }} data-id="element-8126" data-mp="3ifxhhknh3jdp">
              <p className="font-serif italic text-base sm:text-lg text-[#FDFBF7]" data-id="element-8127" data-mp="f3mxhj9vkoxd">
                We create the <span className="text-[#D4AF37]" data-id="element-8128" data-mp="a4jqvue8yana">reach</span>. The
                gallery turns it into <span className="text-white" data-id="element-8129" data-mp="o5nt6bpgn2wx">depth</span>.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Appendix · The Strategic Pipeline — Spark → Scale → Destination */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-12" style={{
        order: 2
      }} data-id="element-8130" data-mp="2pxsev5gd93z7">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-8131" data-mp="3aglw2ojzp5jc" />
          <PunjabiPlacard word="ਰਾਹ" translation="The Path" data-id="element-8132" data-mp="3adzmykhgahz0" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-8133" data-mp="3fdvu2slg2pis">
            {/* Header */}
            <motion.div className="text-center mb-8 sm:mb-10" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-8134" data-mp="3k76sq6g3rzzm">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-8135" data-mp="30glcd1i7knem">
                How It All Connects
              </p>
              <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-[1.1] mb-5" data-id="element-8136" data-mp="frxgrg8ocueq">
                One Pipeline.{' '}
                <span className="text-[#D4AF37]" data-id="element-8137" data-mp="kp76yy3w1a3m">National Scale.</span>
              </h2>
              <p className="text-base sm:text-lg text-white/65 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-8138" data-mp="utduvycri9z3">
                The immersive exhibit isn't the finish line — it's the{' '}
                <span className="text-[#FDFBF7]" data-id="element-8139" data-mp="l3u9om1ku412">starting point</span>. It feeds
                a national engine that ends, deliberately, at the door of the
                ROM gallery the Foundation built.
              </p>
            </motion.div>

            {/* The 3-stage funnel */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-3 items-stretch relative mb-8" data-id="element-8140" data-mp="74uvy9flr9jo">
              {[{
              icon: Sparkles,
              step: 'Stage 01',
              title: 'The Spark',
              detail: 'The immersive exhibit is the emotional first encounter — a felt, unforgettable entry point that makes Sikh history matter to anyone, no museum visit required.'
            }, {
              icon: Globe,
              step: 'Stage 02',
              title: 'The National Engine',
              detail: 'Our schools program and the digital Treasures of the Sikh Empire collectible cards carry that spark across Canada — classroom by classroom, screen by screen, turning one exhibit into a movement.'
            }, {
              icon: Landmark,
              step: 'Stage 03',
              title: 'The Destination',
              detail: 'A generation now invested in their heritage arrives where depth lives — the ROM Gallery of Sikh Art & Culture — becoming the Foundation\u2019s next wave of patrons.'
            }].map((c, i) => <Fragment key={c.title} data-id="element-8141" data-mp="3k9dgl44ro79s" data-mpi={i}>
                  <motion.div className={`rounded-2xl p-6 backdrop-blur-sm relative overflow-hidden border ${i === 2 ? 'bg-black/45 border-[#D4AF37]/45 shadow-[0_0_40px_rgba(212,175,55,0.12)]' : 'bg-black/30 border-[#D4AF37]/15'}`} initial={{
                opacity: 0,
                y: 24
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 0.2 + i * 0.15
              }} viewport={{
                once: true
              }} data-id="element-8142" data-mp="3k8apow7g24tv">
                    <div className="flex items-center gap-3 mb-4" data-id="element-8143" data-mp="1ou6p0ono6r34">
                      <c.icon className="w-7 h-7 text-[#D4AF37] shrink-0" data-id="element-8144" data-mp="1azwat19aufbb" />
                      <span className="text-[#D4AF37]/70 text-[0.65rem] tracking-[0.25em] uppercase" data-id="element-8145" data-mp="1fyz5brrnl351">
                        {c.step}
                      </span>
                    </div>
                    <h3 className="font-serif text-xl text-[#FDFBF7] mb-2 leading-tight" data-id="element-8146" data-mp="28gl94kz1izfy">
                      {c.title}
                    </h3>
                    <p className="text-white/55 font-light text-sm leading-relaxed" data-id="element-8147" data-mp="2ibacl5gcf3v1">
                      {c.detail}
                    </p>
                  </motion.div>
                  {i < 2 && <div className="hidden md:flex absolute top-1/2 z-20 pointer-events-none" style={{
                left: i === 0 ? '33.333%' : '66.666%',
                transform: 'translate(-50%, -50%)'
              }} data-id="element-8148" data-mp="28c1blsn28oz6">
                      <div className="w-9 h-9 rounded-full bg-[#0a0604] border border-[#D4AF37]/50 flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.25)]" data-id="element-8149" data-mp="1ukmnx8i7jzls">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37]" data-id="element-8150" data-mp="1kq4ju28vq21g" />
                      </div>
                    </div>}
                </Fragment>)}
            </div>
          </div>
        </section>

        {/* 18. National Moments */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-8151" data-mp="3n9x6rqvcf0kf">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-8152" data-mp="3raz6s9x33gng" />
          <PunjabiPlacard word="ਲਹਿਰ" translation="Movement" data-id="element-8153" data-mp="5ze6qwgsbtk" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 text-center" data-id="element-8154" data-mp="4fv6sjfbmo3">
            <motion.p className="text-[#D4AF37] text-xs sm:text-sm tracking-[0.4em] uppercase mb-6" initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-8155" data-mp="54kesrfmoxij">
              From the Distillery to the Nation
            </motion.p>
            <motion.h2 className="font-serif text-5xl sm:text-6xl md:text-7xl text-[#FDFBF7] leading-[1.05] mb-6" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2,
            delay: 0.1
          }} viewport={{
            once: true
          }} data-id="element-8156" data-mp="2pvb6negwrfvl">
              This becomes a <br className="hidden sm:block" data-id="element-8157" data-mp="1f2k5yqf7rho8" />
              <span className="text-[#D4AF37]" data-id="element-8158" data-mp="1f5g68lejaifu">Canadian cultural moment.</span>
            </motion.h2>
            <motion.p className="text-lg text-white/60 font-light max-w-2xl mx-auto mb-16" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1,
            delay: 0.3
          }} viewport={{
            once: true
          }} data-id="element-8159" data-mp="1a6cygt765y46">
              A two-stage path from provincial to national recognition — Sikh
              history taking its place on Canada’s biggest stages.
            </motion.p>

            {/* Connected milestone timeline */}
            <div className="relative flex flex-col sm:flex-row items-stretch justify-center gap-8 sm:gap-4" data-id="element-8160" data-mp="1rkwbtfpmwe9n">
              {/* connector line */}
              <div className="hidden sm:block absolute top-7 left-[18%] right-[18%] h-px bg-gradient-to-r from-[#D4AF37]/0 via-[#D4AF37]/50 to-[#D4AF37]/0" data-id="element-8161" data-mp="1vlgainpuvpxw" />

              {[{
              date: 'Nov 2026',
              icon: Landmark,
              title: 'Provincial Moment',
              body: 'Hosted with the Premier of Ontario & Provincial Government.'
            }, {
              date: 'Apr 2027',
              icon: Building2,
              title: 'National Moment',
              body: 'Carried to the Prime Minister & Federal Government in Ottawa.'
            }].map((m, i) => <motion.div key={m.date} className="flex-1 relative flex flex-col items-center" initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.4 + i * 0.2
            }} viewport={{
              once: true
            }} data-id="element-8162" data-mp="js5se2wq01cm" data-mpi={i}>
                  <div className="w-14 h-14 rounded-full bg-[#110c08] border border-[#D4AF37]/50 flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.25)] mb-5 relative z-10" data-id="element-8163" data-mp="tqvd03g03zwk">
                    <m.icon className="w-6 h-6 text-[#D4AF37]" data-id="element-8164" data-mp="1maadbj4af01r" />
                  </div>
                  <p className="font-serif text-2xl text-[#D4AF37] mb-2" data-id="element-8165" data-mp="1hgzd40tzdhit">
                    {m.date}
                  </p>
                  <p className="text-[#FDFBF7] text-sm tracking-widest uppercase mb-2" data-id="element-8166" data-mp="1ouzxn32wl6u8">
                    {m.title}
                  </p>
                  <p className="text-white/60 font-light text-sm max-w-xs" data-id="element-8167" data-mp="1yrl4ltyhgs0p">
                    {m.body}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 19. Ask Context */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-8168" data-mp="27rrez6yvxbvn">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-8169" data-mp="2corkngrtocm8" />
          <PunjabiPlacard word="ਸਰਪ੍ਰਸਤ" translation="Patron" data-id="element-8170" data-mp="2hj5jxu9lv3w6" />

          {/* Ambient glow */}
          <div className="absolute inset-0 z-[1] pointer-events-none bg-[radial-gradient(circle_at_50%_40%,rgba(212,175,55,0.12),transparent_60%)]" data-id="element-8171" data-mp="2mfma0u0d7sbw" />

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6 py-10" data-id="element-8172" data-mp="2vhlszruxx6hh">
            <motion.p className="text-[#D4AF37] text-xs sm:text-sm tracking-[0.4em] uppercase mb-6" initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-8173" data-mp="2vf9btgx0b05h">
              For those who remember
            </motion.p>

            <motion.p className="font-serif italic text-xl sm:text-2xl text-white/70 leading-relaxed max-w-2xl mx-auto mb-8" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1.4,
            delay: 0.15
          }} viewport={{
            once: true
          }} data-id="element-8174" data-mp="2t384yutny0y2">
              We grew up in classrooms that never told our story — or told it
              wrong. We learned to shrink it down to a paragraph.
            </motion.p>

            <motion.h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-[1.1] mb-6" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2,
            delay: 0.3
          }} viewport={{
            once: true
          }} data-id="element-8175" data-mp="ulxwdsctzrz5">
              Now we get to give the next generation{' '}
              <span className="text-[#D4AF37]" data-id="element-8176" data-mp="13lb7epglsolz">the version we deserved.</span>
            </motion.h2>

            <motion.p className="text-lg text-white/60 font-light mb-12 max-w-2xl mx-auto" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1,
            delay: 0.5
          }} viewport={{
            once: true
          }} data-id="element-8177" data-mp="3k9yj4ak9re7f">
              We have committed{' '}
              <span className="text-[#D4AF37] font-medium" data-id="element-8178" data-mp="2lm9vngswpmj3">$400,000</span> to
              begin. Become a Founding Patron and help carry it forward.
            </motion.p>

            <div className="grid sm:grid-cols-2 gap-4 text-left max-w-2xl mx-auto" data-id="element-8179" data-mp="26w2ohx8cnpxp">
              {[{
              icon: GraduationCap,
              label: 'Sponsor your school',
              sub: 'Put your name behind the next class to walk in.'
            }, {
              icon: Users,
              label: 'Fund student access',
              sub: 'No child priced out of their own history.'
            }, {
              icon: Sparkles,
              label: 'Production upgrades',
              sub: 'Make the world even more breathtaking.'
            }, {
              icon: Globe,
              label: 'National expansion',
              sub: 'Carry it city to city, coast to coast.'
            }].map((item, i) => <motion.div key={item.label} className="flex items-start gap-3 border border-[#D4AF37]/25 bg-black/40 p-4 backdrop-blur-sm hover:border-[#D4AF37]/60 transition-colors" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.7,
              delay: 0.6 + i * 0.12
            }} viewport={{
              once: true
            }} data-id="element-8180" data-mp="1e9ayjkrq5b0g" data-mpi={i}>
                  <item.icon className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" data-id="element-8181" data-mp="2e3j2csd66j0s" />
                  <div data-id="element-8182" data-mp="2j1kstycxdkma">
                    <span className="text-[#FDFBF7] text-sm font-medium block" data-id="element-8183" data-mp="2j2vxs1i3wyez">
                      {item.label}
                    </span>
                    <span className="text-white/50 text-xs font-light" data-id="element-8184" data-mp="2nyuinf487yan">
                      {item.sub}
                    </span>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Appendix · Ask Tiers */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" style={{
        order: 2
      }} data-id="element-8185" data-mp="31qitzg6niiqk">
          <SlideAtmosphere overlayGradient="gold" withTexture data-id="element-8186" data-mp="3bpgqp6e8dikn" />
          <PunjabiPlacard word="ਵਿਰਸਾ" translation="Legacy" data-id="element-8187" data-mp="1cdnomvcl5n12" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full" data-id="element-8188" data-mp="1cf8i0hn5yzxc">
            <div className="text-center mb-10" data-id="element-8189" data-mp="1hdjwj5oelylw">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-4" data-id="element-8190" data-mp="1hebnu4ayxa6d">
                Choose your legacy.
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-10" data-id="element-8191" data-mp="151hhmmclunjp">
              {[{
              price: '$10,000',
              title: 'Patron of the Bus',
              icon: Building2,
              items: ['Sponsor student transportation', 'Recognition on sponsor night', 'Invite a school']
            }, {
              price: '$25,000',
              title: 'Founding School Patron',
              icon: Users,
              items: ['Sponsor a school activation', 'Family banner on your night', 'Recognition inside experience']
            }, {
              price: '$50,000',
              title: 'Legacy Patron',
              icon: Crown,
              items: ['Support access & expansion', 'Premium family recognition', 'Maharaja acknowledgement moment']
            }].map((tier, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: i * 0.1
            }} className="border border-[#D4AF37]/30 bg-[#0a0604]/80 p-6 backdrop-blur-md relative overflow-hidden" data-id="element-8192" data-mp="1m9z2yiz0v6rt" data-mpi={i}>
                  <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-8193" data-mp="ol6z8rtt1cle">
                    <tier.icon className="w-20 h-20 text-[#D4AF37]" data-id="element-8194" data-mp="tmw9i597dm8f" />
                  </div>
                  <h3 className="font-serif text-3xl text-[#FDFBF7] mb-2" data-id="element-8195" data-mp="37st3y9fvep7y">
                    {tier.price}
                  </h3>
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-6" data-id="element-8196" data-mp="32tzssno23aay">
                    {tier.title}
                  </p>
                  <ul className="space-y-3" data-id="element-8197" data-mp="3csypd7vjrfhl">
                    {tier.items.map((item, j) => <li key={j} className="text-white/60 text-sm font-light flex gap-2" data-id="element-8198" data-mp="2p2ux1wnt725w" data-mpi={j}>
                        <span className="text-[#D4AF37] opacity-50" data-id="element-8199" data-mp="2k5uqlkn1rxe9">•</span>{' '}
                        {item}
                      </li>)}
                  </ul>
                </motion.div>)}
            </div>

            <div className="text-center flex flex-col items-center" data-id="element-8200" data-mp="25eltyrkq9brm">
              <div className="bg-white p-2 rounded-sm shadow-[0_0_30px_rgba(212,175,55,0.3)] mb-4" data-id="element-8201" data-mp="25cr737pwb9kw">
                <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://typeform.com" alt="Scan to commit" className="w-20 h-20" data-id="element-8202" data-mp="21bph7hhvc9vl" />
              </div>
              <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-8203" data-mp="e2fp2t978pta">
                Scan to commit
              </p>
              <p className="text-white/40 text-[10px] italic font-light" data-id="element-8204" data-mp="j10qux43nhju">
                Contributions may be eligible for tax receipts where applicable.
              </p>
            </div>
          </div>
        </section>

        {/* 20b. The Founder Coin */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-10" data-id="element-8205" data-mp="3rgkd3c6crzpe">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-8206" data-mp="cvmbnfmbsjz" />
          <PunjabiPlacard word="ਸਿੱਕਾ" translation="The Coin" data-id="element-8207" data-mp="16s6372atzrpe" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 w-full flex flex-col items-center text-center" data-id="element-8208" data-mp="16tpml1rsaive">
            <motion.div className="mb-6" initial={{
            opacity: 0,
            y: -20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-8209" data-mp="2w88p6jyieoa2">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-3" data-id="element-8210" data-mp="2ck99cup38qxs">
                An Invitation to This Room
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7]" data-id="element-8211" data-mp="28hcqd2vmystb">
                I am here to invite you in.
              </h2>
            </motion.div>

            <motion.div className="w-full" initial={{
            opacity: 0,
            scale: 0.96
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1,
            delay: 0.2
          }} viewport={{
            once: true
          }} data-id="element-8212" data-mp="1yljg5q5e3bep">
              <FounderCoinCarousel coins={[{
              src: 'https://cdn.magicpatterns.com/uploads/iKFJFRzMvdjc7PQr9DfHUw/image.png',
              label: 'Founder No. FM-0001'
            }, {
              src: 'https://cdn.magicpatterns.com/uploads/jffPzsfZHnE1yXYeZLtNtQ/image.png',
              label: 'Founder No. FM-0010'
            }, {
              src: 'https://cdn.magicpatterns.com/uploads/dznAAvh5j3fw4ifouNnW5D/image.png',
              label: 'Founder No. FM-0011'
            }, {
              src: 'https://cdn.magicpatterns.com/uploads/c9HMsd9FKUmBiJVi913QdX/image.png',
              label: 'Founder No. FM-0012'
            }]} data-id="element-8213" data-mp="36rq8b600jwcj" />
            </motion.div>

            <motion.p className="text-white/60 font-light text-sm max-w-xl mx-auto mt-6" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1,
            delay: 0.4
          }} viewport={{
            once: true
          }} data-id="element-8214" data-mp="1wv9xexm33n8m">
              I am inviting each of you to become a Founding Member of Sikh
              Kingdoms. Your hand-numbered coin marks that decision, and gives
              you <span className="text-[#D4AF37]" data-id="element-8215" data-mp="2phusa81rqhgx">100 invitations</span> to
              bring your own circle into the Lahore Darbar.
            </motion.p>
          </div>
        </section>

        {/* Appendix · FAQ */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" style={{
        order: 2
      }} data-id="element-8216" data-mp="v5ep5g49rjga">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-8217" data-mp="3v8lcnejirckl" />
          <PunjabiPlacard word="ਗਿਆਨ" translation="Knowledge" data-id="element-8218" data-mp="3r81i8iqbyn25" />

          <div className="relative z-10 max-w-4xl mx-auto px-6 w-full" data-id="element-8219" data-mp="3r8unff9n54i6">
            <div className="text-center mb-10" data-id="element-8220" data-mp="3mgb2fx8mj73c">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-3" data-id="element-8221" data-mp="3mezzu0qx05dt">
                Giving &amp; tax, simply.
              </h2>
            </div>

            {/* Cost-after-tax comparison */}
            <div className="border border-[#D4AF37]/30 bg-[#0a0604]/70 p-6 sm:p-8 backdrop-blur-sm mb-6" data-id="element-8222" data-mp="37k25x3g4b7um">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-6 text-center" data-id="element-8223" data-mp="33i70pnv2gabk">
                What it really costs in Ontario
              </p>
              <div className="grid grid-cols-3 gap-3 sm:gap-6" data-id="element-8224" data-mp="2ylgo5aigzwuw">
                {[{
                gift: '$10,000',
                cost: '~$6,000'
              }, {
                gift: '$25,000',
                cost: '~$15,000'
              }, {
                gift: '$50,000',
                cost: '~$30,000'
              }].map(row => <div key={row.gift} className="flex flex-col items-center text-center" data-id="element-8225" data-mp="1e2ea69b7cd3z">
                    <span className="font-serif text-lg sm:text-2xl text-white/50 line-through decoration-[#D4AF37]/40" data-id="element-8226" data-mp="1j2930iejfcro">
                      {row.gift}
                    </span>
                    <ArrowRight className="w-4 h-4 text-[#D4AF37] my-2 rotate-90" data-id="element-8227" data-mp="qdv0pqgf1uab" />
                    <span className="font-serif text-2xl sm:text-4xl text-[#D4AF37] leading-none" data-id="element-8228" data-mp="v9ae6haiho67">
                      {row.cost}
                    </span>
                    <span className="text-white/50 text-[0.6rem] tracking-widest uppercase mt-2" data-id="element-8229" data-mp="26qjwh0hwma5x">
                      after-tax cost
                    </span>
                  </div>)}
              </div>
              <p className="text-[10px] text-white/40 italic mt-6 text-center" data-id="element-8230" data-mp="blssaudow6qr">
                Estimated — charitable credits are non-refundable and depend on
                your personal tax situation.
              </p>
            </div>

            {/* Receipting types — compact */}
            <div className="grid sm:grid-cols-3 gap-4" data-id="element-8231" data-mp="fo7enogkhoms">
              {[{
              k: 'Donation',
              v: 'No benefit received — full charitable receipt.'
            }, {
              k: 'Sponsorship',
              v: 'Marketing value — a business expense, not a receipt.'
            }, {
              k: 'Hybrid',
              v: 'Partial receipt above the value of any benefits.'
            }].map(t => <div key={t.k} className="border border-[#D4AF37]/20 bg-[#0a0604]/50 p-5 backdrop-blur-sm" data-id="element-8232" data-mp="35byc6lasb7qf">
                  <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-2" data-id="element-8233" data-mp="1fwdwyu7ea7rx">
                    {t.k}
                  </p>
                  <p className="text-xs text-white/60 font-light leading-relaxed" data-id="element-8234" data-mp="1konhfupqqfmt">
                    {t.v}
                  </p>
                </div>)}
            </div>

            <p className="text-[10px] text-white/40 italic mt-6 text-center" data-id="element-8235" data-mp="1bvk55pqu08tr">
              General information, not tax advice — please confirm with your
              advisor.
            </p>
          </div>
        </section>

        {/* Appendix · Cartier Collaboration - The Proposal */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-12" style={{
        order: 2
      }} data-id="element-8236" data-mp="1plnni5mlbidx">
          <SlideAtmosphere overlayGradient="dark" withTexture data-id="element-8237" data-mp="e70m4cnzao93" />
          <PunjabiPlacard word="ਮੇਲ" translation="Union" data-id="element-8238" data-mp="e9msi8z4uxpq" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 w-full" data-id="element-8239" data-mp="j769vhr4nqlw">
            {/* Header */}
            <motion.div className="text-center mb-10 sm:mb-12" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-8240" data-mp="ygtcrnbnv7gv">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-8241" data-mp="1hcruda73pn9n">
                A Once-in-a-Century Partnership
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] leading-[1.1] mb-5" data-id="element-8242" data-mp="18dei69rlekl8">
                Two houses of legacy. <br className="hidden sm:block" data-id="element-8243" data-mp="13euxecoqjeug" />
                <span className="text-[#D4AF37]" data-id="element-8244" data-mp="3t4y9rdteys6x">One cultural moment.</span>
              </h2>
              <p className="text-base sm:text-lg text-white/65 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-8245" data-mp="3o5lm4qupxh3a">
                Cartier set the diamonds of the Lahore Treasury. Today, that
                shared history becomes a living collaboration — a chance for the
                world's most storied jeweler to help return Sikh heritage to the
                global stage.
              </p>
            </motion.div>

            {/* What the collaboration unlocks */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8" data-id="element-8246" data-mp="3ss4eh7e5wmij">
              {[{
              icon: Crown,
              title: 'Heritage Reunited',
              detail: 'A narrative that reconnects Cartier with the Maharajas and the jewels of the Lahore Treasury it once set.'
            }, {
              icon: Sparkles,
              title: 'A Global Campaign',
              detail: 'Magazine editorials and billboards bringing the Sikh Empire’s splendor to the streets of the world.'
            }, {
              icon: HeartHandshake,
              title: 'Purpose Built In',
              detail: 'Proceeds support the experience and the schools program — prestige with measurable cultural impact.'
            }].map((c, i) => <motion.div key={c.title} className="bg-black/30 border border-[#D4AF37]/15 rounded-2xl p-6 backdrop-blur-sm" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.15 + i * 0.1
            }} viewport={{
              once: true
            }} data-id="element-8247" data-mp="2e907ag1lupnx" data-mpi={i}>
                  <c.icon className="w-8 h-8 text-[#D4AF37] mb-4" data-id="element-8248" data-mp="36z8fbf2fftos" />
                  <h3 className="font-serif text-xl text-[#FDFBF7] mb-2 leading-tight" data-id="element-8249" data-mp="3pmhiz4fo6aqt">
                    {c.title}
                  </h3>
                  <p className="text-white/55 font-light text-sm leading-relaxed" data-id="element-8250" data-mp="3ukaqq1wmfct7">
                    {c.detail}
                  </p>
                </motion.div>)}
            </div>

            <motion.p className="text-center font-serif italic text-lg sm:text-xl text-[#FDFBF7] max-w-3xl mx-auto" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1,
            delay: 0.5
          }} viewport={{
            once: true
          }} data-id="element-8251" data-mp="3ljkxn5yqw0j6">
              The two empires overlapped for two years.{' '}
              <span className="text-[#D4AF37]" data-id="element-8252" data-mp="2nxxbddw06yld">
                The collaboration can last far longer.
              </span>
            </motion.p>
          </div>
        </section>

        {/* 23. Cartier Collaboration - Magazine */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center" data-id="element-8253" data-mp="33f5chz7tbqvf">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-8254" data-mp="2zd1xj0lxq45e" />
          <PunjabiPlacard word="ਵਿਰਾਸਤ" translation="Legacy" data-id="element-8255" data-mp="2zfei7lj2usm5" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full grid lg:grid-cols-2 gap-12 items-center" data-id="element-8256" data-mp="2udyuflpim802">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} data-id="element-8257" data-mp="2pgoxzm3pkw2q">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4" data-id="element-8258" data-mp="2bptt0kwbyz4f">
                A Legacy of Cultural Exchange
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] mb-6 leading-tight" data-id="element-8259" data-mp="21p9sxlkjs0ns">
                The Sikh Empire & <br data-id="element-8260" data-mp="h6phtzug5tdr" />
                <span className="text-[#D4AF37]" data-id="element-8261" data-mp="h7923n7kktuy">The House of Cartier</span>
              </h2>
              <div className="space-y-6 text-white/80 font-light text-lg" data-id="element-8262" data-mp="m5ag3tq522zk">
                <p data-id="element-8263" data-mp="r6zunjckkyse">
                  Maharaja Ranjit Singh’s court was globally connected,
                  maintaining diplomatic relations with the King of France.
                  French generals trained the Khalsa army, bringing European
                  military precision to the Punjab.
                </p>
                <p data-id="element-8264" data-mp="3h5fuw8ox05i">
                  The Sikh Empire collapsed in{' '}
                  <span className="text-[#FDFBF7] font-medium" data-id="element-8265" data-mp="7hpek48wwbtr">1849</span>. The
                  House of Cartier was founded in Paris in{' '}
                  <span className="text-[#FDFBF7] font-medium" data-id="element-8266" data-mp="c9z1rcin6xsc">1847</span>.
                </p>
                <p className="font-serif italic text-xl text-[#D4AF37] border-l-2 border-[#D4AF37]/30 pl-4 py-2" data-id="element-8267" data-mp="3bvxb6dsak8r4">
                  For two years, these two empires overlapped in time.
                </p>
                <p data-id="element-8268" data-mp="1vhjjkzcohv9q">
                  Ever since, Cartier has been the jeweler of choice for the
                  Maharajas, setting the world's most famous diamonds —
                  including those that once belonged to the Lahore Treasury.
                </p>
              </div>
            </motion.div>

            <motion.div className="relative flex justify-center" initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1,
            delay: 0.2
          }} data-id="element-8269" data-mp="2996eovuvwxqx">
              <div className="relative w-full max-w-md aspect-[2/3] cursor-pointer group" onClick={() => setSelectedImage('https://cdn.magicpatterns.com/uploads/oQ58wnU7hUNtNqVh7jGTxZ/Gemini_Generated_Image_ep18z9ep18z9ep18.png')} data-id="element-8270" data-mp="2s3v7yl99xjq9">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#D4AF37]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" data-id="element-8271" data-mp="lgnw1ggw8uhp" />
                <img src="https://cdn.magicpatterns.com/uploads/oQ58wnU7hUNtNqVh7jGTxZ/Gemini_Generated_Image_ep18z9ep18z9ep18.png" alt="Cartier Magazine Concept" className="w-full h-full object-cover shadow-[0_0_40px_rgba(0,0,0,0.8)] border border-white/10" data-id="element-8272" data-mp="bjsz3209r25j" />
                <div className="absolute inset-0 border border-[#D4AF37]/30 m-4 pointer-events-none" data-id="element-8273" data-mp="z82bxefi8fkj" />
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20" data-id="element-8274" data-mp="v6ql55kw9oar">
                  <span className="bg-black/80 text-[#D4AF37] text-[10px] tracking-widest uppercase px-4 py-2 backdrop-blur-sm border border-[#D4AF37]/30" data-id="element-8275" data-mp="3fxr2lf3b4bsz">
                    Click to Enlarge
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 24. Cartier Collaboration - Billboard */}
        <section className="w-screen h-screen flex-shrink-0 overflow-hidden relative flex items-center justify-center bg-black" data-id="element-8276" data-mp="37gh11l1nuhge">
          <PunjabiPlacard word="ਪ੍ਰਭਾਵ" translation="Impact" data-id="element-8277" data-mp="2yof4hxupplbe" />

          {/* Full-bleed billboard image */}
          <motion.div className="absolute inset-0 cursor-pointer group" initial={{
          opacity: 0,
          scale: 1.04
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1.4,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} onClick={() => setSelectedImage('https://cdn.magicpatterns.com/uploads/6CMkBmPZYB8UPFpRm8EZSo/Gemini_Generated_Image_z4wlxz4wlxz4wlxz.png')} data-id="element-8278" data-mp="33evhjgumf4x8">
            <img src="https://cdn.magicpatterns.com/uploads/6CMkBmPZYB8UPFpRm8EZSo/Gemini_Generated_Image_z4wlxz4wlxz4wlxz.png" alt="Cartier Billboard Concept" className="w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-[1200ms]" data-id="element-8279" data-mp="1w5lqadfzedmr" />
            {/* Readability scrims top & bottom */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-transparent to-black/70 pointer-events-none" data-id="element-8280" data-mp="2f23s5nojntwq" />

            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20" data-id="element-8281" data-mp="2sw4l20stemp6">
              <span className="bg-black/80 text-[#D4AF37] text-[10px] tracking-widest uppercase px-4 py-2 backdrop-blur-sm border border-[#D4AF37]/30" data-id="element-8282" data-mp="32rengpn4kw3h">
                Click to Enlarge
              </span>
            </div>
          </motion.div>

          {/* Overlaid heading */}
          <motion.div className="relative z-10 text-center px-6 mt-[-40vh] pointer-events-none" initial={{
          opacity: 0,
          y: -20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.3
        }} viewport={{
          once: true
        }} data-id="element-8283" data-mp="3lwkpdv02cp7t">
            <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4 drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]" data-id="element-8284" data-mp="32y8g8995jfc8">
              The Vision
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#FDFBF7] max-w-3xl mx-auto drop-shadow-[0_4px_16px_rgba(0,0,0,0.95)]" data-id="element-8285" data-mp="3cwefsk7hk9dx">
              Bringing the legacy to the streets of the world.
            </h2>
          </motion.div>
        </section>

        {/* 25. The Lahore Darbar Leadership Lab — MBA capstone */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-12" data-id="element-8286" data-mp="2cn6cah5xhndc">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-8287" data-mp="22y4ymxmuvi2j" />
          <PunjabiPlacard word="ਅਗਵਾਈ" translation="Leadership" data-id="element-8288" data-mp="22wbrlh8byvu0" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 w-full flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-8289" data-mp="1xztn8igwwzi7">
            {/* Left Column: Ivey Brand & Title */}
            <motion.div className="flex-1 text-left w-full" initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} data-id="element-8290" data-mp="1oxu5fny2pre7">
              <div className="flex items-center gap-6 mb-8" data-id="element-8291" data-mp="39fic5fdq88ln">
                <div className="bg-white rounded-md px-5 py-3 inline-flex shadow-[0_8px_30px_rgba(0,0,0,0.5)]" data-id="element-8292" data-mp="17pb54i6gmg2m">
                  <img src="https://cdn.magicpatterns.com/uploads/8RSDZDCFJC94F2N5gDFaR9/pasted-image.png" alt="Ivey Business School, Western University, Canada" className="h-10 sm:h-12 w-auto object-contain" data-id="element-8293" data-mp="1cg1b9xmpmmyd" />
                </div>
                <div className="h-px bg-white/20 flex-1 hidden sm:block" data-id="element-8294" data-mp="1qgkfsuryvavs"></div>
                <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase hidden sm:block" data-id="element-8295" data-mp="1vf5ir1yls8nx">
                  MBA Capstone
                </p>
              </div>

              <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#FDFBF7] leading-[1.1] mb-6" data-id="element-8296" data-mp="1hh74dwka8c8f">
                The Lahore Darbar <br data-id="element-8297" data-mp="5z62z20hbkzb" />
                <span className="text-[#D4AF37] italic" data-id="element-8298" data-mp="5ymhxcfj8gh2">Leadership Lab</span>
              </h2>

              <div className="flex items-start gap-6 mt-12 pt-8 border-t border-white/10" data-id="element-8299" data-mp="a2c5bykh0s0i">
                <span className="font-serif text-6xl text-[#D4AF37] leading-none shrink-0" data-id="element-8300" data-mp="2gystplw3yc8">
                  #2
                </span>
                <p className="text-white/70 font-light text-sm sm:text-base leading-relaxed max-w-sm" data-id="element-8301" data-mp="3tnt7iq9ip1bs">
                  Ivey is the{' '}
                  <span className="text-white font-medium" data-id="element-8302" data-mp="7h4dumxnmmlc">
                    second-largest producer of case studies in the world
                  </span>
                  , behind only Harvard. Becoming a capstone here puts Sikh
                  history in front of Canada’s future leaders.
                </p>
              </div>
            </motion.div>

            {/* Right Column: The Case & Imagery */}
            <motion.div className="flex-1 w-full relative" initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1,
            delay: 0.2
          }} viewport={{
            once: true
          }} data-id="element-8303" data-mp="3kv7pxdhmh0oj">
              {/* Decorative glow */}
              <div className="absolute -inset-10 bg-gradient-to-br from-[#D4AF37]/10 via-transparent to-transparent rounded-full blur-3xl z-0 pointer-events-none" data-id="element-8304" data-mp="2gm6n5gxct3hv"></div>

              <div className="relative z-10 bg-black/40 border border-white/10 rounded-2xl p-8 sm:p-10 backdrop-blur-md shadow-2xl" data-id="element-8305" data-mp="2klfiurbyfbg2">
                <p className="font-serif italic text-2xl sm:text-3xl lg:text-4xl text-[#FDFBF7] leading-snug mb-10" data-id="element-8306" data-mp="2ul6dct0ilqg5">
                  “How do you build institutions strong enough to{' '}
                  <span className="text-[#D4AF37]" data-id="element-8307" data-mp="34b9g3kxfuiro">
                    outlive a brilliant founder?
                  </span>
                  ”
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-8308" data-mp="38exna2i5rcce">
                  {['https://cdn.magicpatterns.com/uploads/uPbfJuRcUJBTsH767Ydzun/richard-ivey-building-southwest-corner.jpg', 'https://cdn.magicpatterns.com/uploads/eei1VL3A4XPSuCegXUhYvS/145-ellisdon-ivey-school-of-business-edit.jpg'].map(src => <div key={src} className="relative overflow-hidden rounded-lg cursor-pointer group border border-white/5" onClick={() => setSelectedImage(src)} data-id="element-8309" data-mp="soywenxp197g">
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-500 z-10" data-id="element-8310" data-mp="1ge094ndk76ij"></div>
                      <img src={src} alt="Ivey Business School" className="w-full h-32 sm:h-40 object-cover grayscale-[50%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" data-id="element-8311" data-mp="1bm05okya5vh7" />
                    </div>)}
                </div>

                <div className="flex items-center justify-between text-[0.65rem] sm:text-xs tracking-[0.2em] uppercase text-white/50 border-t border-white/10 pt-6" data-id="element-8312" data-mp="1r64rikdfacgl">
                  <span className="text-white/90" data-id="element-8313" data-mp="1n6m9pxgwehhz">Prepare</span>
                  <span className="text-[#D4AF37]/50" data-id="element-8314" data-mp="215lu2sxdoa8c">→</span>
                  <span className="text-white/90" data-id="element-8315" data-mp="1w5hdl2wgzfi4">Step Inside</span>
                  <span className="text-[#D4AF37]/50" data-id="element-8316" data-mp="1w46al5aumbs2">→</span>
                  <span className="text-[#D4AF37]" data-id="element-8317" data-mp="2a4h4kh7gg6hg">Advise the Darbar</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Conclusion · Thank You + WhatsApp */}
        <section className="w-screen h-screen flex-shrink-0 overflow-y-auto relative flex items-center justify-center py-8" style={{
        order: 1
      }} data-id="element-8318" data-mp="2juk3sz566go8">
          <SlideAtmosphere overlayGradient="royal" withTexture data-id="element-8319" data-mp="2tsga9dn5wng0" />
          <PunjabiPlacard word="ਧੰਨਵਾਦ" translation="Thank You" data-id="element-8320" data-mp="2ou553g7tb144" />

          <div className="relative z-10 mx-auto grid w-full max-w-6xl items-center gap-8 px-6 lg:grid-cols-[0.78fr_1.22fr] lg:gap-12" data-id="element-8321" data-mp="2ovzqswqwqx99">
            <motion.div className="text-center lg:text-left" initial={{
            opacity: 0,
            x: -18
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.28,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-8322" data-mp="3chjwiao7ekiy">
              <p className="text-[#D4AF37] text-xs tracking-[0.35em] uppercase mb-6" data-id="element-8323" data-mp="2orz6j7tmun06">
                Life in the Sikh Kingdoms
              </p>
              <h2 className="font-serif text-5xl sm:text-6xl text-[#FDFBF7] leading-tight mb-6" data-id="element-8324" data-mp="2tjh9u2j4ozob">
                Thank you for your time.
              </h2>
              <p className="font-serif text-2xl sm:text-3xl text-[#D4AF37] mb-7" data-id="element-8325" data-mp="2kq49ifiqivs7">
                Let&rsquo;s continue on WhatsApp.
              </p>
              <p className="text-white/65 text-lg font-light leading-relaxed max-w-md mx-auto lg:mx-0" data-id="element-8326" data-mp="25tmxrn6mvf54">
                Scan the QR code to send Ash a WhatsApp message and continue the
                Founding Member conversation.
              </p>
              <p className="mt-8 inline-flex border border-[#D4AF37]/30 bg-black/30 px-5 py-3 text-sm font-medium text-[#FDFBF7]" data-id="element-8327" data-mp="1mx4ld6m7h1wb">
                Open your camera, scan, then tap the WhatsApp link
              </p>
              <p className="mt-8 text-[10px] uppercase tracking-[0.28em] text-white/30" data-id="element-8328" data-mp="1czgejibbs3xs">
                Appendix follows
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.28,
            delay: 0.06
          }} className="flex justify-center lg:justify-end" data-id="element-8329" data-mp="2ak2zvyqrlzrl">
              <img src="https://cdn.magicpatterns.com/uploads/cJpfguVaXU99Q46xb7eJgm/WhatsApp_Image_2026-09-19_at_10.33.55.jpg" alt="WhatsApp contact QR code for Ash Singh" className="max-h-[78vh] w-auto max-w-full border border-[#D4AF37]/35 bg-black object-contain shadow-[0_24px_70px_rgba(0,0,0,0.55)]" data-id="element-8330" data-mp="1vriz34r384db" />
            </motion.div>
          </div>
        </section>
      </motion.div>

      {/* Lightbox Modal */}
      <AnimatePresence data-id="element-8331" data-mp="35ty8ctras8ei">
        {selectedImage && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 sm:p-8 cursor-pointer" onClick={() => setSelectedImage(null)} data-id="element-8332" data-mp="3amrbjd74b8oh">
            <motion.img initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} src={selectedImage} alt="Enlarged view" className="max-w-full max-h-full object-contain rounded-sm shadow-[0_0_50px_rgba(212,175,55,0.15)]" data-id="element-8333" data-mp="3tln0oa44cwvg" />
            <div className="absolute top-6 right-6 text-white/50 text-sm font-light tracking-widest uppercase" data-id="element-8334" data-mp="3uo2fuoqfztmi">
              Close
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};